import React, {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import '../PanelContainer.scss';
import PanelOverlay from './PanelOverlay';
import SimpleContextMenu, {
  SimpleContextMenuItem,
} from '@components/ContextMenu/SimpleContextMenu';
import { PanelCommonProps } from 'config/panelConfig';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { usePanelContainer } from '../usePanelContainer';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { BranchInfo } from 'utils/FunctionUtilites';
import {
  cleanUpVtkRenderer,
  ContextKey,
  createCrossSection,
  CrossSectionContext,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleMouseWheel,
  initializeVtkRenderer,
  labelorlineonoff,
  setContainer,
  getTaviCenterTangentAtIndex,
  handleNodeChange,
  updataAorticNode,
  updateMidCrossSection,
  updateRenderWindow,
  useBufferPool,
} from './vtkManagers';
import MultiBarChart from '@components/BarChart/BarChart';
import {
  registerRenderer,
  scheduleRender,
} from '@features/analysis/rendering/Panels/renderState';
import { useBranchInfo } from '@contexts/BranchContext';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkPoints from '@kitware/vtk.js/Common/Core/Points';
import vtkCellArray from '@kitware/vtk.js/Common/Core/CellArray';
import vtkOpenGLHardwareSelector from '@kitware/vtk.js/Rendering/OpenGL/HardwareSelector';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';
import { FieldAssociations } from '@kitware/vtk.js/Common/DataModel/DataSet/Constants';
import { mat4, vec3 } from 'gl-matrix';
import { useConfig } from 'config/useConfig';
import {
  normalizeLesionStackSpacingMm,
  normalizeLesionStackThicknessMm,
  type Label,
} from 'config/config';
import useAuth from '@hooks/Auth/useAuth';
import {
  makeCoronaryColorPoints,
  makeOpacityPoints,
  rgbToNormalizedARGB,
} from 'config/colorUtils';
import {
  getLabelVolumeFromSeriesId,
  getVolumeFromSeriesId,
} from './CornerstoneManager';
import { colorandopacitycontrol2D } from 'utils/vtkUtilites';
import { getTaviCenterIdx } from './taviDataUtils';
import ImageLayerStack, { type ImageLayerStackWheelEvent } from '../ImageLayerStack';
import {
  getBranchImageResource,
  getCrossSectionImageAsset,
  getCprCrossSectionImagesFromParameter,
  filterImageLayerVisibilityForAnalysisType,
  isCprCrossSectionImageModeEnabled,
  buildPickPointFromCrossSectionAsset,
  buildCrossSectionImagePrefetchPlan,
  resolveCrossSectionLayerPathsWithRevision,
  type ImageLayerKey,
  type CrossSectionFrameRef,
} from '../cprImageResources';
import { getConfiguredCprCrossSectionImageMode } from '../cprImageModeConfig';
import {
  getEffectiveCprCrossSectionImageMode,
  shouldForceLegacyCprForFfrLumenEdit,
} from '../cprImageModeState';
import { getCrossSectionMetricOverlay } from '../crossSectionMetrics';
import {
  registerLocalOverlayImageContext,
  withLocalOverlayFallbackLayers,
} from '../localOverlayImageProvider';
import {
  iscprCrossSectionImagesBranchDeferred,
  loadcprCrossSectionImagesBranchResource,
  mergecprCrossSectionImagesBranchResource,
} from '../cprCrossSectionImages';
import {
  isServerCprFrontDataBranchDeferred,
  isServerCprFrontDataImageManifest,
  loadServerCprFrontDataBranchResourceOnce,
} from '../frontDataCprDebugLoader';
import type { ImageWindowingPolicy } from '../imageWindowing';
import ServerCrossSectionCanvas from './ServerCrossSectionCanvas';



function makeMarkerPreviewPrefetchPlan() {
  return {
    layers: [],
    strategy: 'radius' as const,
    estimatedDecodedBytes: 0,
    budgetBytes: 0,
    radius: 0,
    candidateCount: 0,
    selectedCount: 0,
    visibleLayerCount: 0,
    orderedIndexes: [],
  };
}

export function getComponentInfoFromClass(className: string): {
  name: string;
  index: number;
} {
  const csMatch = className.split('-');
  if (csMatch) {
    const number = csMatch[2];
    const number2 = csMatch[1];
    const sectionMap: Record<string, string> = {
      '0': 'Proximal',
      '1': 'Mid',
      '2': 'Distal',
    };
    let name = sectionMap[number];

    if (!name && Number.isFinite(Number(number))) {
      name = `Cross Section ${Number(number) + 1}`;
    }
    if (Number(number2) === 3) {
      name = 'Cross-section';
    }
    if (name) {
      return { name, index: Number(number2) };
    }
  }

  const cprMatch = className.split('-');
  if (cprMatch) {
    const number = cprMatch[1];
    const nameMap: Record<string, string> = {
      '20': 'Histogram',
      '21': 'Graph',
    };
    const name = nameMap[number];
    if (name) {
      return { name, index: Number(number) };
    }
  }

  return { name: 'Unknown', index: -1 };
}

function isImageManifestUsableForCurrentAnalysis({
  manifest,
  resourceMode,
  analysisType,
}: {
  manifest: ReturnType<typeof getCprCrossSectionImagesFromParameter>;
  resourceMode: string | null | undefined;
  analysisType: string | null | undefined;
}) {
  if (!manifest) return false;
  if (resourceMode !== 'server') return true;
  if (!manifest.revision?.startsWith('front-data:')) return true;
  if (!analysisType) return true;
  return manifest.revision.split(':').includes(analysisType);
}

type CrossSectionCameraSnapshot = {
  position: [number, number, number];
  focalPoint: [number, number, number];
  viewUp: [number, number, number];
  parallelScale: number;
};

const PLAQUE_CPR_STACK_CROSS_SECTION_COUNT = 7;
const PLAQUE_CPR_STACK_MIDDLE_COUNT = PLAQUE_CPR_STACK_CROSS_SECTION_COUNT - 2;
const PLAQUE_CPR_STACK_LENGTH_EPSILON_MM = 1e-3;
type LesionStackCrossSectionIndex = number | null;

function getLesionStackCrossSectionRendererId(mainIndex: number, stackSlot: number) {
  if (stackSlot <= 0) return `CrossSection.${mainIndex}.0`;
  if (stackSlot >= PLAQUE_CPR_STACK_CROSS_SECTION_COUNT - 1) {
    return `CrossSection.${mainIndex}.2`;
  }
  return `CrossSection.3.${stackSlot - 1}`;
}

function findNearestLengthIndex(
  lengthArray: ArrayLike<number>,
  targetLength: number,
  startIndex: number,
  endIndex: number,
) {
  const last = lengthArray.length - 1;
  const from = Math.max(0, Math.min(last, Math.min(startIndex, endIndex)));
  const to = Math.max(0, Math.min(last, Math.max(startIndex, endIndex)));
  let bestIndex = from;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let index = from; index <= to; index += 1) {
    const length = Number(lengthArray[index]);
    if (!Number.isFinite(length)) continue;
    const distance = Math.abs(length - targetLength);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  }

  return bestIndex;
}

function getLesionStackMiddleCapacity(
  proximalLength: number,
  distalLength: number,
  spacingMm: number,
) {
  const distance = Math.abs(distalLength - proximalLength);
  if (!Number.isFinite(distance)) return 0;
  return Math.max(
    0,
    Math.min(
      PLAQUE_CPR_STACK_MIDDLE_COUNT,
      Math.floor(
        (distance - PLAQUE_CPR_STACK_LENGTH_EPSILON_MM) /
          spacingMm,
      ),
    ),
  );
}

function isLesionStackLengthBetween(
  value: number,
  proximalLength: number,
  distalLength: number,
) {
  return proximalLength <= distalLength
    ? value > proximalLength && value < distalLength
    : value < proximalLength && value > distalLength;
}

function buildLesionStackCrossSectionIndexes({
  totalInfo,
  lengthArray,
  frontExpand,
  middleAnchorFullIndex,
  spacingMm,
}: {
  totalInfo: Array<{ index?: number }>;
  lengthArray: ArrayLike<number> | null | undefined;
  frontExpand: number;
  middleAnchorFullIndex?: number;
  spacingMm: number;
}): LesionStackCrossSectionIndex[] {
  const proximalFullIndex = Number(totalInfo[0]?.index);
  const storedMiddleFullIndex = Number(totalInfo[1]?.index);
  const distalFullIndex = Number(totalInfo[2]?.index);

  if (
    !lengthArray?.length ||
    !Number.isFinite(proximalFullIndex) ||
    !Number.isFinite(distalFullIndex)
  ) {
    return [];
  }

  const last = lengthArray.length - 1;
  const proximalLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(proximalFullIndex - frontExpand)),
  );
  const distalLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(distalFullIndex - frontExpand)),
  );
  const proximalLength = Number(lengthArray[proximalLocalIndex]);
  const distalLength = Number(lengthArray[distalLocalIndex]);

  if (!Number.isFinite(proximalLength) || !Number.isFinite(distalLength)) {
    return [
      Math.round(proximalFullIndex),
      ...Array.from({ length: PLAQUE_CPR_STACK_MIDDLE_COUNT }, () => null),
      Math.round(distalFullIndex),
    ];
  }

  const direction = distalLength >= proximalLength ? 1 : -1;
  const middleCapacity = getLesionStackMiddleCapacity(
    proximalLength,
    distalLength,
    spacingMm,
  );
  const indices: LesionStackCrossSectionIndex[] = [
    Math.round(proximalFullIndex),
  ];

  if (middleCapacity <= 0) {
    indices.push(
      ...Array.from({ length: PLAQUE_CPR_STACK_MIDDLE_COUNT }, () => null),
    );
    indices.push(Math.round(distalFullIndex));
    return indices;
  }

  const fallbackAnchorLength =
    proximalLength + direction * spacingMm;
  const candidateAnchorFullIndex = Number.isFinite(middleAnchorFullIndex)
    ? Number(middleAnchorFullIndex)
    : storedMiddleFullIndex;
  const candidateAnchorLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(candidateAnchorFullIndex - frontExpand)),
  );
  const candidateAnchorLength = Number(lengthArray[candidateAnchorLocalIndex]);
  let anchorLength = Number.isFinite(candidateAnchorLength)
    ? candidateAnchorLength
    : fallbackAnchorLength;
  const shouldUseFallbackAnchor =
    !isLesionStackLengthBetween(anchorLength, proximalLength, distalLength) ||
    (direction > 0
      ? anchorLength < fallbackAnchorLength
      : anchorLength > fallbackAnchorLength);
  if (shouldUseFallbackAnchor) {
    anchorLength = fallbackAnchorLength;
  }

  for (let slot = 0; slot < PLAQUE_CPR_STACK_MIDDLE_COUNT; slot += 1) {
    if (slot >= middleCapacity) {
      indices.push(null);
      continue;
    }
    const targetLength =
      anchorLength + direction * spacingMm * slot;
    if (
      !isLesionStackLengthBetween(targetLength, proximalLength, distalLength)
    ) {
      indices.push(null);
      continue;
    }
    const localIndex = findNearestLengthIndex(
      lengthArray,
      targetLength,
      proximalLocalIndex,
      distalLocalIndex,
    );
    indices.push(localIndex + frontExpand);
  }

  indices.push(Math.round(distalFullIndex));
  return indices;
}

function getLesionStackDefaultMiddleAnchorIndex({
  totalInfo,
  lengthArray,
  frontExpand,
  spacingMm,
}: {
  totalInfo: Array<{ index?: number }>;
  lengthArray: ArrayLike<number> | null | undefined;
  frontExpand: number;
  spacingMm: number;
}) {
  const indexes = buildLesionStackCrossSectionIndexes({
    totalInfo: [totalInfo[0], { index: Number.NaN }, totalInfo[2]],
    lengthArray,
    frontExpand,
    spacingMm,
  });
  return indexes[1] ?? undefined;
}

function isLesionStackMiddleAnchorAllowed({
  totalInfo,
  lengthArray,
  frontExpand,
  middleAnchorFullIndex,
  spacingMm,
}: {
  totalInfo: Array<{ index?: number }>;
  lengthArray: ArrayLike<number> | null | undefined;
  frontExpand: number;
  middleAnchorFullIndex: number;
  spacingMm: number;
}) {
  const proximalFullIndex = Number(totalInfo[0]?.index);
  const distalFullIndex = Number(totalInfo[2]?.index);
  if (
    !lengthArray?.length ||
    !Number.isFinite(proximalFullIndex) ||
    !Number.isFinite(distalFullIndex) ||
    !Number.isFinite(middleAnchorFullIndex)
  ) {
    return false;
  }

  const last = lengthArray.length - 1;
  const proximalLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(proximalFullIndex - frontExpand)),
  );
  const distalLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(distalFullIndex - frontExpand)),
  );
  const middleAnchorLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(middleAnchorFullIndex - frontExpand)),
  );
  const proximalLength = Number(lengthArray[proximalLocalIndex]);
  const distalLength = Number(lengthArray[distalLocalIndex]);
  const middleAnchorLength = Number(lengthArray[middleAnchorLocalIndex]);
  if (
    !Number.isFinite(proximalLength) ||
    !Number.isFinite(distalLength) ||
    !Number.isFinite(middleAnchorLength)
  ) {
    return false;
  }

  const middleCapacity = getLesionStackMiddleCapacity(
    proximalLength,
    distalLength,
    spacingMm,
  );
  if (middleCapacity <= 0) return false;
  const direction = distalLength >= proximalLength ? 1 : -1;
  for (let slot = 0; slot < middleCapacity; slot += 1) {
    const targetLength =
      middleAnchorLength + direction * spacingMm * slot;
    if (
      !isLesionStackLengthBetween(targetLength, proximalLength, distalLength)
    ) {
      return false;
    }
  }
  return true;
}

function resolveLesionStackMiddleAnchorIndexBySpacingStep({
  totalInfo,
  lengthArray,
  frontExpand,
  currentAnchorFullIndex,
  spacingMm,
  stepCount,
}: {
  totalInfo: Array<{ index?: number }>;
  lengthArray: ArrayLike<number> | null | undefined;
  frontExpand: number;
  currentAnchorFullIndex: number;
  spacingMm: number;
  stepCount: number;
}) {
  const proximalFullIndex = Number(totalInfo[0]?.index);
  const distalFullIndex = Number(totalInfo[2]?.index);
  if (
    !lengthArray?.length ||
    !Number.isFinite(proximalFullIndex) ||
    !Number.isFinite(distalFullIndex) ||
    !Number.isFinite(currentAnchorFullIndex) ||
    !Number.isFinite(spacingMm) ||
    spacingMm <= 0 ||
    !Number.isFinite(stepCount) ||
    stepCount === 0
  ) {
    return undefined;
  }

  const last = lengthArray.length - 1;
  const proximalLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(proximalFullIndex - frontExpand)),
  );
  const distalLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(distalFullIndex - frontExpand)),
  );
  const currentAnchorLocalIndex = Math.max(
    0,
    Math.min(last, Math.round(currentAnchorFullIndex - frontExpand)),
  );
  const proximalLength = Number(lengthArray[proximalLocalIndex]);
  const distalLength = Number(lengthArray[distalLocalIndex]);
  const currentAnchorLength = Number(lengthArray[currentAnchorLocalIndex]);
  if (
    !Number.isFinite(proximalLength) ||
    !Number.isFinite(distalLength) ||
    !Number.isFinite(currentAnchorLength)
  ) {
    return undefined;
  }

  const direction = distalLength >= proximalLength ? 1 : -1;
  const targetLength =
    currentAnchorLength + direction * spacingMm * Math.trunc(stepCount);
  if (!isLesionStackLengthBetween(targetLength, proximalLength, distalLength)) {
    return undefined;
  }

  const nextLocalIndex = findNearestLengthIndex(
    lengthArray,
    targetLength,
    proximalLocalIndex,
    distalLocalIndex,
  );
  return nextLocalIndex + frontExpand;
}

const CrossSectionPanel: React.FC<PanelCommonProps> = ({
  className,
  panelConfig,
}: PanelCommonProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const context = useRef<CrossSectionContext>(null);
  const {
    switchConfigs,
    globalToolState,
    panelToolState,
    currentVOI,
    selectedLayoutConfig,
    updateWindowLevelWidth,
  } = useAnalysisPage();
  const {
    analysisType,
    panelStates,
    updateCPRState,
    selectedPanelClassName,
    fullscreenPanelComponentKey,
    updateSelectedPanelConfig,
  } = usePanelContainer();
  const { selectedSeriesId } = useBrowserPage();
  const { accessToken } = useAuth();
  const { bufferPool, contextPool, setBufferPool, setContextPool } =
    useBufferPool();
  const { branchIds } = useBranchInfo();
  const { config, setData } = useConfig();
  const lesionStackSpacingMm = normalizeLesionStackSpacingMm(
    config.lesionStackSpacingMm,
  );
  const lesionStackThicknessMm = normalizeLesionStackThicknessMm(
    config.lesionStackThicknessMm,
  );
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const panelState = panelStates['CPR'].coronary;
  if (!panelState || !panelState.current || !panelState.state) return;

  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const isAorticMode =
    analysisType === 'aorticValveCalcium' || analysisType === 'tavi';
  const crossSectionAnalysisType = isAorticMode
    ? 'aorticValveCalcium'
    : analysisType;

  const cprMode = useMemo(
    () =>
      isAorticMode
        ? { name: 'Cross-section', index: 0 }
        : getComponentInfoFromClass(className),
    [className, isAorticMode],
  );

  const [rawMainIndex, subIndex, rendererId] = useMemo(() => {
    const [main, sub] = className.split('-').slice(1).map(Number);
    return [main, sub, `CrossSection.${main}.${sub}`];
  }, [className]);
  const isLesionStackViewLayout =
    analysisType === 'plaque' && selectedLayoutConfig.type === 'lesionStackView';
  const mainIndex =
    isLesionStackViewLayout && rawMainIndex === 3 ? 0 : rawMainIndex;
  const lesionStackSlotIndex = useMemo(() => {
    if (!isLesionStackViewLayout) return subIndex;
    if (rawMainIndex === 0 && subIndex === 0) return 0;
    if (rawMainIndex === 0 && subIndex === 2) {
      return PLAQUE_CPR_STACK_CROSS_SECTION_COUNT - 1;
    }
    if (rawMainIndex === 3 && subIndex >= 0 && subIndex < PLAQUE_CPR_STACK_MIDDLE_COUNT) {
      return subIndex + 1;
    }
    return subIndex;
  }, [isLesionStackViewLayout, rawMainIndex, subIndex]);
  const sourceSubIndex = useMemo(() => {
    if (!Number.isFinite(subIndex)) return 0;
    if (subIndex >= 0 && subIndex <= 2) return subIndex;
    return ((subIndex % 3) + 3) % 3;
  }, [subIndex]);
  const stackStateSubIndex = useMemo(() => {
    if (lesionStackSlotIndex === 0) return 0;
    if (lesionStackSlotIndex === PLAQUE_CPR_STACK_CROSS_SECTION_COUNT - 1) return 2;
    return 1;
  }, [lesionStackSlotIndex]);
  const effectiveSourceSubIndex = isLesionStackViewLayout
    ? stackStateSubIndex
    : sourceSubIndex;
  const isLesionStackMiddlePanel =
    isLesionStackViewLayout &&
    lesionStackSlotIndex > 0 &&
    lesionStackSlotIndex < PLAQUE_CPR_STACK_CROSS_SECTION_COUNT - 1;
  const isAnyFullscreenActive =
    fullscreenPanelComponentKey !== undefined &&
    fullscreenPanelComponentKey !== 'Empty';
  const isTaviFixedPanel =
    analysisType === 'tavi' &&
    ((mainIndex === 1 && subIndex >= 0 && subIndex <= 2) ||
      (mainIndex === 2 && subIndex >= 0 && subIndex <= 2));
  const isTaviAnnulusPanel =
    analysisType === 'tavi' && mainIndex === 1 && subIndex === 0;
  const [isTaviManualMode, setIsTaviManualMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return Boolean((window as any).__taviManualMode);
  });
  const shouldShowOutlineFeatures = false;
  const shouldShowAnnulusPointEdit = false;
  // Temporary: hide metric text box overlay while keeping outline/axis rendering.
  const showOutlineMetricsBox = false;
  const isFinite2DPoint = (p: [number, number] | null | undefined): p is [number, number] =>
    !!p && Number.isFinite(p[0]) && Number.isFinite(p[1]);

  const aorticBranchId =
    analysisType === 'tavi'
      ? (config.tavi_aorta_id ?? config.aorta_id)
      : config.aorta_id;
  const aorticCurrentIndex =
    analysisType === 'tavi'
      ? (config.tavi_current_index ?? config.aorta_current_index)
      : config.aorta_current_index;
  const branchId = isAorticMode
    ? aorticBranchId
    : panelState.current[mainIndex];
  const fallbackIndex = isAorticMode
    ? Number(aorticCurrentIndex ?? 0)
    : Number(config.coronary_front_expand ?? 0);
  const totalInfo = panelState.state[branchId] ?? [{ index: fallbackIndex }];
  const lesionStackBranchGraphDataset = pool?.branchGraphDataset?.data as
    | BranchInfo
    | undefined;
  const lesionStackLengthArray = lesionStackBranchGraphDataset?.getLengthArray(branchId);
  const lesionStackCrossSectionIndexes = useMemo(() => {
    if (!isLesionStackViewLayout || isAorticMode) return [];
    return buildLesionStackCrossSectionIndexes({
      totalInfo,
      lengthArray: lesionStackLengthArray,
      frontExpand: config.coronary_front_expand,
      spacingMm: lesionStackSpacingMm,
    });
  }, [
    branchId,
    config.coronary_front_expand,
    isAorticMode,
    isLesionStackViewLayout,
    lesionStackLengthArray,
    lesionStackSpacingMm,
    totalInfo[0]?.index,
    totalInfo[1]?.index,
    totalInfo[2]?.index,
  ]);
  const sourceInfo = totalInfo[effectiveSourceSubIndex] ?? totalInfo[0] ?? { index: fallbackIndex };
  const stackCrossSectionIndex =
    isLesionStackViewLayout &&
    !isAorticMode &&
    lesionStackSlotIndex >= 0 &&
    lesionStackSlotIndex < PLAQUE_CPR_STACK_CROSS_SECTION_COUNT
      ? lesionStackCrossSectionIndexes[lesionStackSlotIndex]
      : undefined;
  const resolvedStackCrossSectionIndex = Number.isFinite(stackCrossSectionIndex)
    ? Number(stackCrossSectionIndex)
    : undefined;
  const isLesionStackHiddenMiddlePanel =
    isLesionStackMiddlePanel && stackCrossSectionIndex === null;
  const stackCenterlineData =
    resolvedStackCrossSectionIndex !== undefined && pool?.centerData.loaded
      ? (pool.centerData.data as Record<number, vtkPolyData> | undefined)?.[
          branchId
        ]
      : undefined;
  const stackNodeData =
    resolvedStackCrossSectionIndex !== undefined && stackCenterlineData
      ? handleNodeChange(stackCenterlineData, resolvedStackCrossSectionIndex)
      : null;
  const info =
    resolvedStackCrossSectionIndex !== undefined
      ? stackNodeData
        ? {
            position: stackNodeData.position,
            normal: stackNodeData.normal,
            index: resolvedStackCrossSectionIndex,
          }
        : { ...sourceInfo, index: resolvedStackCrossSectionIndex }
      : isLesionStackHiddenMiddlePanel
        ? { ...sourceInfo, index: -1 }
        : sourceInfo;
  const branchIndices = useMemo(
    () =>
      isAorticMode || totalInfo.length === 1
        ? [totalInfo[0].index]
        : [totalInfo[0].index, totalInfo[1].index, totalInfo[2].index],
    [
      isAorticMode,
      totalInfo.length,
      totalInfo[0]?.index,
      totalInfo[1]?.index,
      totalInfo[2]?.index,
    ],
  );

  const configuredCprCrossSectionImageMode =
    getConfiguredCprCrossSectionImageMode();
  const forceLegacyCprForFfrLumenEdit = shouldForceLegacyCprForFfrLumenEdit(
    analysisType,
    config.EditMode,
  );
  const cprCrossSectionImageMode = getEffectiveCprCrossSectionImageMode(
    configuredCprCrossSectionImageMode,
    pool,
    !forceLegacyCprForFfrLumenEdit,
  );
  const isImageModeEnabled = isCprCrossSectionImageModeEnabled(
    cprCrossSectionImageMode,
  );
  const imageManifest = useMemo(() => {
    if (!isImageModeEnabled) return null;
    const candidate =
      getCprCrossSectionImagesFromParameter(pool?.cprCrossSectionImages?.data) ??
      getCprCrossSectionImagesFromParameter(pool?.parameter?.data);
    return isImageManifestUsableForCurrentAnalysis({
      manifest: candidate,
      resourceMode: cprCrossSectionImageMode,
      analysisType,
    })
      ? candidate
      : null;
  }, [
    analysisType,
    cprCrossSectionImageMode,
    isImageModeEnabled,
    pool?.cprCrossSectionImages?.data,
    pool?.parameter?.data,
  ]);
  useEffect(() => {
    if (
      !isImageModeEnabled ||
      !imageManifest ||
      !selectedSeriesId ||
      !accessToken ||
      !analysisType ||
      branchId === undefined
    ) {
      return;
    }

    const shouldLoadFrontDataBranch = isServerCprFrontDataBranchDeferred(
      imageManifest,
      branchId,
    );
    const shouldLoadManifestBranch = iscprCrossSectionImagesBranchDeferred(
      imageManifest,
      branchId,
    );

    if (!shouldLoadFrontDataBranch && !shouldLoadManifestBranch) return;

    let cancelled = false;
    const loadBranch = shouldLoadFrontDataBranch
      ? loadServerCprFrontDataBranchResourceOnce({
          accessToken,
          seriesId: selectedSeriesId,
          manifest: imageManifest,
          branchId,
          analysisType,
          currentVOI,
        })
      : loadcprCrossSectionImagesBranchResource({
          accessToken,
          seriesId: selectedSeriesId,
          manifest: imageManifest,
          branchId,
          CacheControl: {
            Pragma: 'no-cache',
            Expires: '0',
          },
        });

    loadBranch
      .then(({ branchId: loadedBranchId, branch }) => {
        if (cancelled) return;
        setBufferPool((prev) => {
          const currentSeries = prev[selectedSeriesId];
          const currentItem = currentSeries?.cprCrossSectionImages;
          if (!currentSeries || !currentItem) return prev;
          const currentManifest = getCprCrossSectionImagesFromParameter(
            currentItem?.data,
          );
          if (
            !currentManifest ||
            currentManifest.revision !== imageManifest.revision
          ) {
            return prev;
          }
          const branchKey = String(loadedBranchId);
          if (currentManifest.branches?.[branchKey]?.loaded === true) {
            return prev;
          }
          return {
            ...prev,
            [selectedSeriesId]: {
              ...currentSeries,
              cprCrossSectionImages: {
                ...currentItem,
                data: mergecprCrossSectionImagesBranchResource(
                  currentManifest,
                  loadedBranchId,
                  branch,
                ),
              },
            },
          };
        });
      })
      .catch((err) => {
        console.warn(
          '[CrossSectionPanel] failed to load CPR image branch',
          err,
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    analysisType,
    branchId,
    imageManifest,
    imageManifest?.revision,
    isImageModeEnabled,
    selectedSeriesId,
    setBufferPool,
  ]);
  const branchImageResource = getBranchImageResource(imageManifest, branchId);
  const crossSectionImageStack = branchImageResource?.crossSection;
  const isServerFrontDataImageManifest =
    isServerCprFrontDataImageManifest(imageManifest);
  const isAorticServerFrontDataMode =
    analysisType === 'aorticValveCalcium' &&
    isImageModeEnabled &&
    isServerFrontDataImageManifest;
  const shouldUseServerCrossSectionCanvas =
    isImageModeEnabled &&
    isServerFrontDataImageManifest &&
    (!isAorticMode || isAorticServerFrontDataMode);
  const serverCrossSectionInitialZoomScale = isLesionStackViewLayout ? 4.5 : undefined;
  const serverCrossSectionSlabThicknessMm =
    isLesionStackViewLayout && !isAorticMode ? lesionStackThicknessMm : undefined;
  const hasServerFrameVectorCrossSection =
    isServerFrontDataImageManifest &&
    !!crossSectionImageStack?.frameByIndex &&
    Object.keys(crossSectionImageStack.frameByIndex).length > 0;

  // front_data only supplies CPR HU/label/mapping plus frame_vectors.json.
  // Cross-section pixels are still generated from the existing volume/label data
  // through the legacy VTK reslice path.  If we keep the image shell enabled
  // here, the panel has no real base image resource and only a gray placeholder
  // is shown.  Server mode therefore uses VTK for cross-section, with optional
  // axes from frame_vectors.json.
  const shouldUseCrossSectionImageShell =
    isImageModeEnabled && !isServerFrontDataImageManifest;

  const getServerCrossSectionFrameRef = useCallback(
    (index: number): CrossSectionFrameRef | null => {
      const frameByIndex = crossSectionImageStack?.frameByIndex;
      if (!frameByIndex) return null;
      const exact = frameByIndex[index];
      if (exact) return exact;

      let best: CrossSectionFrameRef | null = null;
      let bestDistance = Number.POSITIVE_INFINITY;
      Object.entries(frameByIndex).forEach(([key, frame]) => {
        const frameIndex = Number(key);
        if (!Number.isFinite(frameIndex)) return;
        const distance = Math.abs(frameIndex - index);
        if (distance < bestDistance) {
          best = frame;
          bestDistance = distance;
        }
      });
      return best;
    },
    [crossSectionImageStack?.frameByIndex],
  );

  const getServerFrameVectorAxes = useCallback(
    (index: number, imageData?: vtkImageData | null): mat4 | undefined => {
      if (!hasServerFrameVectorCrossSection) return undefined;
      const frame = getServerCrossSectionFrameRef(index);
      const rawWorld = frame?.world;
      const rawXAxis = frame?.tangent;
      const rawYAxis = frame?.binormal;
      const rawZAxis = frame?.normal;
      if (!rawWorld || !rawXAxis || !rawYAxis || !rawZAxis) return undefined;

      const spacing =
        (imageData?.getSpacing?.() as [number, number, number] | undefined) ??
        [1, 1, 1];
      const direction =
        (imageData?.getDirection?.() as number[] | undefined) ??
        [1, 0, 0, 0, 1, 0, 0, 0, 1];
      const origin =
        (imageData?.getOrigin?.() as [number, number, number] | undefined) ??
        [0, 0, 0];
      const lowRate =
        config.generalSetting.renderingQuality !== 'high' ? 0.5 : 1;

      const toWorldVec = (v: [number, number, number]) => {
        const scaled = vec3.fromValues(
          v[0] * spacing[0],
          v[1] * spacing[1],
          v[2] * spacing[2],
        );
        const out = vec3.fromValues(
          direction[0] * scaled[0] +
            direction[3] * scaled[1] +
            direction[6] * scaled[2],
          direction[1] * scaled[0] +
            direction[4] * scaled[1] +
            direction[7] * scaled[2],
          direction[2] * scaled[0] +
            direction[5] * scaled[1] +
            direction[8] * scaled[2],
        );
        if (vec3.sqrLen(out) < 1e-12) return null;
        vec3.normalize(out, out);
        return out;
      };

      const toWorldPoint = (p: [number, number, number]) => {
        const scaledPoint: [number, number, number] = [
          p[0] * lowRate,
          p[1] * lowRate,
          p[2] * lowRate,
        ];
        const vec = vec3.fromValues(
          scaledPoint[0] * spacing[0],
          scaledPoint[1] * spacing[1],
          scaledPoint[2] * spacing[2],
        );
        const out = vec3.fromValues(
          direction[0] * vec[0] + direction[3] * vec[1] + direction[6] * vec[2],
          direction[1] * vec[0] + direction[4] * vec[1] + direction[7] * vec[2],
          direction[2] * vec[0] + direction[5] * vec[1] + direction[8] * vec[2],
        );
        out[0] += origin[0];
        out[1] += origin[1];
        out[2] += origin[2];
        return out;
      };

      const xAxis = toWorldVec(rawXAxis);
      const yAxis = toWorldVec(rawYAxis);
      const zAxis = toWorldVec(rawZAxis);
      if (!xAxis || !yAxis || !zAxis) return undefined;

      const world = toWorldPoint(rawWorld);
      const axes = mat4.create();
      axes[0] = xAxis[0];
      axes[1] = xAxis[1];
      axes[2] = xAxis[2];
      axes[3] = 0;
      axes[4] = yAxis[0];
      axes[5] = yAxis[1];
      axes[6] = yAxis[2];
      axes[7] = 0;
      axes[8] = zAxis[0];
      axes[9] = zAxis[1];
      axes[10] = zAxis[2];
      axes[11] = 0;
      axes[12] = world[0];
      axes[13] = world[1];
      axes[14] = world[2];
      axes[15] = 1;
      return axes;
    },
    [
      config.generalSetting.renderingQuality,
      getServerCrossSectionFrameRef,
      hasServerFrameVectorCrossSection,
    ],
  );
  const coronaryCenterlinePointCount = useMemo(() => {
    if (isAorticMode) return undefined;

    const centerlineData = pool?.centerData.loaded
      ? (pool.centerData.data as Record<number | string, vtkPolyData> | undefined)?.[
          branchId
        ]
      : undefined;
    const vtkPointCount = Number(centerlineData?.getNumberOfPoints?.());
    if (Number.isFinite(vtkPointCount) && vtkPointCount > 0) {
      return Math.floor(vtkPointCount);
    }

    const frontExpand = Math.max(
      0,
      Math.floor(Number(config.coronary_front_expand) || 0),
    );
    const stackIndexStart = Number(crossSectionImageStack?.indexStart);
    const stackIncludesFrontExpand =
      Number.isFinite(stackIndexStart) && stackIndexStart < frontExpand;
    if (!stackIncludesFrontExpand) return undefined;

    const pathMaxIndex = Object.values(branchImageResource?.cpr ?? {}).reduce(
      (maxIndex, cprResource) => {
        cprResource?.angleFrames?.forEach((frame) => {
          frame.centerlinePath?.forEach((point) => {
            const sourceIndex = Number(point?.sourceCenterlineIndex);
            const index = Number.isFinite(sourceIndex)
              ? sourceIndex
              : Number(point?.index);
            if (Number.isFinite(index)) {
              maxIndex = Math.max(maxIndex, Math.floor(index));
            }
          });
        });
        return maxIndex;
      },
      -1,
    );
    if (pathMaxIndex >= 0) return pathMaxIndex + 1;

    const frameMaxIndex = Object.keys(crossSectionImageStack?.frameByIndex ?? {}).reduce(
      (maxIndex, key) => {
        const index = Number(key);
        return Number.isFinite(index)
          ? Math.max(maxIndex, Math.floor(index))
          : maxIndex;
      },
      -1,
    );
    if (frameMaxIndex >= 0) return frameMaxIndex + 1;

    return undefined;
  }, [
    branchId,
    branchImageResource?.cpr,
    config.coronary_front_expand,
    crossSectionImageStack?.frameByIndex,
    crossSectionImageStack?.indexStart,
    isAorticMode,
    pool?.centerData.data,
    pool?.centerData.loaded,
  ]);

  const effectiveCrossSectionImageRange = useMemo(() => {
    if (!crossSectionImageStack) return null;

    let start = Math.floor(Number(crossSectionImageStack.indexStart));
    let end = Math.floor(Number(crossSectionImageStack.indexEnd));
    if (!Number.isFinite(start)) start = 0;
    if (!Number.isFinite(end)) end = start;

    if (!isAorticMode) {
      const frontExpand = Math.max(
        0,
        Math.floor(Number(config.coronary_front_expand) || 0),
      );
      const backExpand = Math.max(
        0,
        Math.floor(Number(config.coronary_back_expand) || 0),
      );
      start = Math.max(start, frontExpand);

      if (
        Number.isFinite(coronaryCenterlinePointCount) &&
        (coronaryCenterlinePointCount ?? 0) > 0
      ) {
        end = Math.min(
          end,
          Math.max(start, (coronaryCenterlinePointCount ?? 0) - 1 - backExpand),
        );
      }
    }

    return {
      start,
      end: Math.max(start, end),
    };
  }, [
    config.coronary_back_expand,
    config.coronary_front_expand,
    coronaryCenterlinePointCount,
    crossSectionImageStack?.indexEnd,
    crossSectionImageStack?.indexStart,
    isAorticMode,
  ]);

  const isCrossSectionImageIndexAllowed = useCallback(
    (candidateIndex: number) => {
      if (!Number.isFinite(candidateIndex)) return false;
      const range = effectiveCrossSectionImageRange;
      if (!range) return true;
      return candidateIndex >= range.start && candidateIndex <= range.end;
    },
    [effectiveCrossSectionImageRange],
  );

  const resolveCrossSectionImageIndex = useCallback(
    (candidateIndex?: number | null) => {
      const fallbackCandidates = [
        candidateIndex,
        isAorticMode ? aorticCurrentIndex : undefined,
        isAorticMode ? config.aorta_start_index : undefined,
        effectiveCrossSectionImageRange?.start,
        config.coronary_front_expand,
        crossSectionImageStack?.indexStart,
      ];
      const finiteCandidates = fallbackCandidates
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value >= 0);

      if (!crossSectionImageStack) {
        return finiteCandidates[0] ?? 0;
      }

      const rangeStart =
        effectiveCrossSectionImageRange?.start ?? crossSectionImageStack.indexStart;
      const rangeEnd =
        effectiveCrossSectionImageRange?.end ?? crossSectionImageStack.indexEnd;

      const inRange = finiteCandidates.find(
        (value) => value >= rangeStart && value <= rangeEnd,
      );
      if (inRange !== undefined) return inRange;

      const fallback = finiteCandidates[0] ?? rangeStart;
      return Math.max(rangeStart, Math.min(rangeEnd, fallback));
    },
    [
      aorticCurrentIndex,
      config.aorta_start_index,
      config.coronary_front_expand,
      crossSectionImageStack?.indexEnd,
      crossSectionImageStack?.indexStart,
      effectiveCrossSectionImageRange,
      isAorticMode,
    ],
  );
  const getDefaultCrossSectionImageIndex = useCallback(() => {
    return resolveCrossSectionImageIndex(info?.index);
  }, [
    info?.index,
    resolveCrossSectionImageIndex,
  ]);
  const [imageIndex, setImageIndex] = useState(
    getDefaultCrossSectionImageIndex,
  );
  const imageIndexRef = useRef(imageIndex);
  const [cprMarkerPreviewMode, setCprMarkerPreviewMode] = useState<'none' | 'single' | 'group'>('none');
  const cprMarkerPreviewModeRef = useRef<'none' | 'single' | 'group'>(cprMarkerPreviewMode);
  const isCprMarkerPreviewActive = cprMarkerPreviewMode !== 'none';
  const cprMarkerPreviewResetTimerRef = useRef<number | null>(null);
  useEffect(() => {
    imageIndexRef.current = imageIndex;
  }, [imageIndex]);
  useEffect(() => {
    cprMarkerPreviewModeRef.current = cprMarkerPreviewMode;
  }, [cprMarkerPreviewMode]);
  useEffect(
    () => () => {
      if (cprMarkerPreviewResetTimerRef.current !== null) {
        window.clearTimeout(cprMarkerPreviewResetTimerRef.current);
        cprMarkerPreviewResetTimerRef.current = null;
      }
    },
    [],
  );
  useEffect(() => {
    const nextIndex = getDefaultCrossSectionImageIndex();
    setImageIndex(nextIndex);
    setNewIndex(
      isAorticMode ? nextIndex : nextIndex - config.coronary_front_expand,
    );
    setAxisOverlay(null);
    setOutlineMetrics(null);
  }, [
    branchId,
    config.coronary_front_expand,
    getDefaultCrossSectionImageIndex,
    imageManifest?.revision,
    isAorticMode,
    mainIndex,
    effectiveSourceSubIndex,
    subIndex,
  ]);
  const rawCrossSectionImageLayers = useMemo(
    () =>
      resolveCrossSectionLayerPathsWithRevision(
        imageManifest,
        branchId,
        imageIndex,
      ),
    [branchId, imageIndex, imageManifest],
  );
  const crossSectionImageLayers = useMemo(
    () =>
      withLocalOverlayFallbackLayers(rawCrossSectionImageLayers, {
        seriesId: selectedSeriesId,
        kind: 'cross-section',
        branchId,
        index: imageIndex,
      }),
    [branchId, imageIndex, rawCrossSectionImageLayers, selectedSeriesId],
  );
  const crossSectionOverlayLabel = useMemo(
    () => config.viewOverlayLabels?.crossSection ?? config.label,
    [config.label, config.viewOverlayLabels?.crossSection],
  );

  const crossSectionImageLayerVisibility = useMemo(() => {
    const next = filterImageLayerVisibilityForAnalysisType(
      analysisType,
      crossSectionOverlayLabel as Partial<Record<ImageLayerKey, boolean>>,
    );

    const hiddenLabelVisibility = {
      ...next,
      lumen: false,
      lumenoutline: false,
      plaque: false,
      plaqueoutline: false,
      fai: false,
      aorta: false,
      avc: false,
    } as Partial<Record<ImageLayerKey, boolean>>;

    if (isLesionStackViewLayout) {
      return hiddenLabelVisibility;
    }

    // During coronary group marker drag, three CrossSection panels can update
    // at the same time. Keep the preview path light by showing the HU/base
    // frame only while dragging, then restore labels/overlays on mouse up.
    if (cprMarkerPreviewMode === 'group') {
      return hiddenLabelVisibility;
    }

    return next;
  }, [analysisType, crossSectionOverlayLabel, cprMarkerPreviewMode, isLesionStackViewLayout]);
  const crossSectionImagePrefetchPlan = useMemo(
    () => {
      if (isCprMarkerPreviewActive) return makeMarkerPreviewPrefetchPlan();
      return buildCrossSectionImagePrefetchPlan({
        manifest: imageManifest,
        branchId,
        activeIndex: imageIndex,
        revision: imageManifest?.revision,
        visibility: crossSectionImageLayerVisibility,
      });
    },
    [
      branchId,
      crossSectionImageLayerVisibility,
      imageIndex,
      imageManifest,
      imageManifest?.revision,
      isCprMarkerPreviewActive,
    ],
  );
  const crossSectionImagePrefetchLayers = isCprMarkerPreviewActive
    ? []
    : crossSectionImagePrefetchPlan.layers;
  const crossSectionImageOverlayPolicy = useMemo(
    () => ({
      analysisType: crossSectionAnalysisType as any,
      config: config as Record<string, any>,
    }),
    [config, crossSectionAnalysisType],
  );
  const crossSectionImageCacheScope = useMemo(
    () => ({
      seriesId: selectedSeriesId,
      revision: imageManifest?.revision,
      branchId,
      view: 'cross-section',
      resourceMode: cprCrossSectionImageMode,
    }),
    [
      branchId,
      cprCrossSectionImageMode,
      imageManifest?.revision,
      selectedSeriesId,
    ],
  );
  const crossSectionImageDiagnostic = useMemo(
    () =>
      [
        `strategy=${crossSectionImagePrefetchPlan.strategy}`,
        `prefetch=${crossSectionImagePrefetchPlan.selectedCount}/${crossSectionImagePrefetchPlan.candidateCount}`,
        `estimate=${crossSectionImagePrefetchPlan.estimatedDecodedBytes}`,
        `index=${imageIndex}`,
        `range=${crossSectionImageStack?.indexStart ?? 'n/a'}-${crossSectionImageStack?.indexEnd ?? 'n/a'}`,
      ].join(';'),
    [
      crossSectionImagePrefetchPlan,
      crossSectionImageStack?.indexEnd,
      crossSectionImageStack?.indexStart,
      imageIndex,
    ],
  );

  // VTK 시작한다
  const hasLabelData = pool?.labelData.loaded;
  const hasCenterData =
    pool?.centerData.loaded &&
    !!(pool.centerData.data as Record<number, vtkPolyData>)[branchId];
  const hasTaviTangentData =
    analysisType !== 'tavi' || !!(pool as any)?.['tavi.centerTangent']?.loaded;
  const hasTaviHeightPlaneData =
    analysisType !== 'tavi' ||
    mainIndex !== 2 ||
    !!(pool as any)?.['tavi.heightPlane']?.loaded;
  const hasBranchGraphDataset = pool?.branchGraphDataset.loaded;
  const branchGraphDataset = pool?.branchGraphDataset.data as BranchInfo;
  useLayoutEffect(() => {
    if (!shouldUseCrossSectionImageShell || !selectedSeriesId) return;
    const labelVolume = getLabelVolumeFromSeriesId(
      selectedSeriesId,
      config.generalSetting?.renderingQuality !== 'high',
    );
    registerLocalOverlayImageContext(selectedSeriesId, {
      analysisType,
      config,
      currentVOI,
      pool,
      labelDataByBranch: pool?.labelData.data as
        | Record<number, vtkImageData>
        | undefined,
      centerDataByBranch: pool?.centerData.data as
        | Record<number, vtkPolyData>
        | undefined,
      aorticLabelData: labelVolume?.imageData as vtkImageData | undefined,
    });
  }, [
    analysisType,
    config,
    currentVOI,
    pool,
    selectedSeriesId,
    shouldUseCrossSectionImageShell,
    shouldUseServerCrossSectionCanvas,
    getServerFrameVectorAxes,
  ]);
  const isDataReady =
    hasLabelData &&
    hasCenterData &&
    hasTaviTangentData &&
    hasTaviHeightPlaneData;
  const [newIndex, setNewIndex] = useState<number | null>(null);
  const [hoverIndexCoord, setHoverIndexCoord] = useState<
    [number, number, number] | null
  >(null);
  const [hoverHU, setHoverHU] = useState<number | null>(null);
  const [outlineMetrics, setOutlineMetrics] = useState<{
    shortAxis: number;
    longAxis: number;
    area: number;
    diameter: number;
  } | null>(null);
  const [axisOverlay, setAxisOverlay] = useState<{
    long: [[number, number], [number, number]];
    short: [[number, number], [number, number]];
  } | null>(null);
  const [taviRelativeOutlineOverlay, setTaviRelativeOutlineOverlay] = useState<
    Array<[number, number]> | null
  >(null);
  const [taviRelativeExtraLinesOverlay, setTaviRelativeExtraLinesOverlay] =
    useState<Array<[[number, number], [number, number]]> | null>(null);
  const [taviHeightLineOverlay, setTaviHeightLineOverlay] = useState<{
    annulusLine: [[number, number], [number, number]];
    connector: [[number, number], [number, number]];
    ostium: [number, number];
    projected: [number, number];
    cusp: [number, number];
  } | null>(null);
  const [annulusEditablePoints, setAnnulusEditablePoints] = useState<
    Array<[number, number]> | null
  >(null);
  const annulusDraggingHandleIndexRef = useRef<number>(-1);
  const fixedPanelZoomAppliedKeyRef = useRef<string | null>(null);
  const serverCameraScopeKeyRef = useRef<string | null>(null);
  const outlineDragRef = useRef<{
    isDragging: boolean;
    lastClientX: number;
    lastClientY: number;
    mode: 'none' | 'translate' | 'vertex';
    vertexIndex: number;
  }>({
    isDragging: false,
    lastClientX: 0,
    lastClientY: 0,
    mode: 'none',
    vertexIndex: -1,
  });
  const hoverPickRafRef = useRef<number | null>(null);
  const hoverPickPointRef = useRef<{ clientX: number; clientY: number } | null>(
    null,
  );
  const windowLevelCommitRafRef = useRef<number | null>(null);
  const pendingWindowingPolicyRef = useRef<ImageWindowingPolicy | null>(null);
  const { windowLevel, windowWidth, invert } = currentVOI;
  const crossSectionImageWindowingPolicy = useMemo<ImageWindowingPolicy>(
    () => ({
      windowLevel,
      windowWidth,
      invert,
    }),
    [invert, windowLevel, windowWidth],
  );
  const handleCrossSectionImageWindowLevelChange = useCallback(
    (next: ImageWindowingPolicy) => {
      pendingWindowingPolicyRef.current = next;
      if (windowLevelCommitRafRef.current !== null) return;

      windowLevelCommitRafRef.current = window.requestAnimationFrame(() => {
        windowLevelCommitRafRef.current = null;
        const pending = pendingWindowingPolicyRef.current;
        pendingWindowingPolicyRef.current = null;
        if (!pending) return;
        updateWindowLevelWidth([pending.windowLevel, pending.windowWidth]);
      });
    },
    [updateWindowLevelWidth],
  );

  useEffect(
    () => () => {
      if (windowLevelCommitRafRef.current !== null) {
        window.cancelAnimationFrame(windowLevelCommitRafRef.current);
        windowLevelCommitRafRef.current = null;
      }
      pendingWindowingPolicyRef.current = null;
    },
    [],
  );

  const captureCrossSectionCamera = useCallback((): CrossSectionCameraSnapshot | null => {
    const camera = context.current?.renderer?.getActiveCamera?.();
    if (!camera) return null;
    const parallelScale = camera.getParallelScale?.();
    if (!Number.isFinite(parallelScale) || (parallelScale as number) <= 0) {
      return null;
    }
    const position = camera.getPosition?.() as [number, number, number] | undefined;
    const focalPoint = camera.getFocalPoint?.() as [number, number, number] | undefined;
    const viewUp = camera.getViewUp?.() as [number, number, number] | undefined;
    if (!position || !focalPoint || !viewUp) return null;

    return {
      position: [position[0], position[1], position[2]],
      focalPoint: [focalPoint[0], focalPoint[1], focalPoint[2]],
      viewUp: [viewUp[0], viewUp[1], viewUp[2]],
      parallelScale: parallelScale as number,
    };
  }, []);

  const restoreCrossSectionCamera = useCallback(
    (snapshot: CrossSectionCameraSnapshot | null) => {
      if (!snapshot || !context.current) return;
      const camera = context.current.renderer?.getActiveCamera?.();
      if (!camera) return;

      camera.setPosition(...snapshot.position);
      camera.setFocalPoint(...snapshot.focalPoint);
      camera.setViewUp?.(...snapshot.viewUp);
      camera.setParallelProjection?.(true);
      camera.setParallelScale(snapshot.parallelScale);
      context.current.zoom = snapshot.parallelScale;
      context.current.renderer?.resetCameraClippingRange?.();
    },
    [],
  );

  const applyFixedPanelZoom = useCallback((force: boolean = false) => {
      if (!isTaviFixedPanel || !context.current) return;
      const applyKey = [
        selectedSeriesId ?? 'none',
        analysisType,
        branchId,
        mainIndex,
        subIndex,
      ].join(':');
      if (!force && fixedPanelZoomAppliedKeyRef.current === applyKey) return;

      const camera = context.current.renderer?.getActiveCamera?.();
      if (!camera) return;
      const ctxZoom = context.current['zoom'] as number | undefined;
      const baseParallelScale =
        Number.isFinite(ctxZoom) && (ctxZoom as number) > 0
          ? (ctxZoom as number)
          : camera.getParallelScale?.();
    if (!Number.isFinite(baseParallelScale) || (baseParallelScale as number) <= 0) return;

      // Slight initial zoom-in for fixed TAVI views (non-wheel panels).
      camera.setParallelScale((baseParallelScale as number) * 0.72);
      context.current.renderer?.resetCameraClippingRange?.();
      fixedPanelZoomAppliedKeyRef.current = applyKey;
      context.current.renderWindow?.render?.();
  }, [
      isTaviFixedPanel,
      selectedSeriesId,
      analysisType,
      branchId,
      mainIndex,
      subIndex,
  ]);

  useEffect(() => {
    const onModeChanged = (evt: Event) => {
      const detail = (evt as CustomEvent<{ enabled?: boolean }>).detail;
      setIsTaviManualMode(Boolean(detail?.enabled));
    };
    window.addEventListener('tavi-manual-mode-changed', onModeChanged);
    return () => {
      window.removeEventListener('tavi-manual-mode-changed', onModeChanged);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (hoverPickRafRef.current !== null) {
        window.cancelAnimationFrame(hoverPickRafRef.current);
        hoverPickRafRef.current = null;
      }
      hoverPickPointRef.current = null;
    };
  }, []);

  type TaviPlaneData = {
    center: [number, number, number];
    normal: [number, number, number];
    dx: [number, number, number];
    dy: [number, number, number];
  };

  const buildTaviAxesFromPlane = useCallback((plane: TaviPlaneData | null) => {
      if (!plane) return null;
    const imageData = context.current?.['imageData'] as vtkImageData | undefined;
    const spacing = (imageData?.getSpacing?.() as [number, number, number]) ?? [
      1, 1, 1,
    ];
      const direction = (imageData?.getDirection?.() as number[]) ?? [
        1, 0, 0, 0, 1, 0, 0, 0, 1,
      ];
      const origin = (imageData?.getOrigin?.() as [number, number, number]) ?? [
        0, 0, 0,
      ];

      const toWorldVec = (v: [number, number, number]) => {
        const s = vec3.fromValues(
          v[0] * spacing[0],
          v[1] * spacing[1],
          v[2] * spacing[2],
        );
        return vec3.fromValues(
          direction[0] * s[0] + direction[3] * s[1] + direction[6] * s[2],
          direction[1] * s[0] + direction[4] * s[1] + direction[7] * s[2],
          direction[2] * s[0] + direction[5] * s[1] + direction[8] * s[2],
        );
      };
    const lowRate = config.generalSetting.renderingQuality !== 'high' ? 0.5 : 1;
      const toWorldPoint = (p: [number, number, number]) => {
        const scaledPoint: [number, number, number] = [
          p[0] * lowRate,
          p[1] * lowRate,
          p[2] * lowRate,
        ];
        const w = toWorldVec(scaledPoint);
      return vec3.fromValues(w[0] + origin[0], w[1] + origin[1], w[2] + origin[2]);
      };

      const xAxis = toWorldVec(plane.dx);
      const yAxis = toWorldVec(plane.dy);
      const zAxis = toWorldVec(plane.normal);
      if (vec3.sqrLen(xAxis) < 1e-12) return null;
      if (vec3.sqrLen(yAxis) < 1e-12) return null;
      if (vec3.sqrLen(zAxis) < 1e-12) return null;
      vec3.normalize(xAxis, xAxis);
      vec3.normalize(yAxis, yAxis);
      vec3.normalize(zAxis, zAxis);

      const center = toWorldPoint(plane.center);
      const m = mat4.create();
      m[0] = xAxis[0];
      m[1] = xAxis[1];
      m[2] = xAxis[2];
      m[4] = yAxis[0];
      m[5] = yAxis[1];
      m[6] = yAxis[2];
      m[8] = zAxis[0];
      m[9] = zAxis[1];
      m[10] = zAxis[2];
      m[12] = center[0];
      m[13] = center[1];
      m[14] = center[2];
      return m;
  }, [config.generalSetting.renderingQuality, context.current?.imageData]);

  const taviPanelAxes = useMemo(() => {
    if (analysisType !== 'tavi') return null;
    const parsed = (pool as Record<string, any> | undefined)?.['tavi.parsed']
      ?.data as Record<string, any> | undefined;
    if (!parsed) return null;

    const isValidPlane = (row: unknown): row is TaviPlaneData => {
      const p = row as TaviPlaneData | null | undefined;
      return !!(
        p &&
        Array.isArray(p.center) &&
        Array.isArray(p.normal) &&
        Array.isArray(p.dx) &&
        Array.isArray(p.dy)
      );
    };

    const readVecAtIndex = (
      arrLike: unknown,
      idx: number,
    ): [number, number, number] | null => {
      if (typeof idx !== 'number' || idx < 0) return null;
      const a =
        arrLike instanceof Float64Array
          ? arrLike
          : arrLike instanceof ArrayBuffer
            ? new Float64Array(arrLike)
            : ArrayBuffer.isView(arrLike)
              ? new Float64Array(
                  arrLike.buffer,
                  arrLike.byteOffset,
                  Math.floor(arrLike.byteLength / Float64Array.BYTES_PER_ELEMENT),
                )
              : null;
      if (!a) return null;
      const p = idx * 3;
      if (p + 2 >= a.length) return null;
      const x = Number(a[p + 0]);
      const y = Number(a[p + 1]);
      const z = Number(a[p + 2]);
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
        return null;
      }
      return [x, y, z];
    };

    const getPlaneForPanel = (): TaviPlaneData | null => {
      if (mainIndex === 1) {
        // annulus/sinus/stj row does not have center/normal/dx/dy.
        // It carries centerIdx, so build plane from centerline vector arrays.
        const keyByPanel =
          subIndex === 0
            ? 'annulus'
            : subIndex === 1
              ? 'sinus'
              : subIndex === 2
                ? 'sinotubularjunction'
                : null;
        if (!keyByPanel) return null;
        const overridePlane = config.taviPlaneOverrides?.[keyByPanel];
        if (overridePlane) return overridePlane;
        const row = parsed?.[keyByPanel];
        if (isValidPlane(row)) return row;

        const centerIdx = Number((row as { centerIdx?: number } | null)?.centerIdx);
        if (!Number.isFinite(centerIdx)) return null;

        const center = readVecAtIndex(parsed?.centerline, centerIdx);
        const normal = readVecAtIndex(parsed?.centerTangent, centerIdx);
        const dx = readVecAtIndex(parsed?.center_dx, centerIdx);
        const dy = readVecAtIndex(parsed?.center_dy, centerIdx);
        if (!center || !normal || !dx || !dy) return null;

        return { center, normal, dx, dy };
      }

      if (mainIndex === 2) {
        const planesRaw = parsed?.heightPlane as
          | Array<TaviPlaneData>
          | {
              lca?: TaviPlaneData;
              rca?: TaviPlaneData;
              stj?: TaviPlaneData;
            }
          | undefined;

        const row = Array.isArray(planesRaw)
          ? planesRaw?.[subIndex]
          : subIndex === 0
            ? planesRaw?.lca
            : subIndex === 1
              ? planesRaw?.rca
              : planesRaw?.stj;
        return isValidPlane(row) ? row : null;
      }

      return null;
    };

    return buildTaviAxesFromPlane(getPlaneForPanel());
  }, [
    analysisType,
    mainIndex,
    subIndex,
    pool,
    buildTaviAxesFromPlane,
    config.taviPlaneOverrides?.annulus,
    config.taviPlaneOverrides?.sinus,
    config.taviPlaneOverrides?.sinotubularjunction,
  ]);

  const taviPanelTitle = useMemo(() => {
    if (analysisType !== 'tavi') return null;
    if (mainIndex === 1 && subIndex === 0) return 'Annulus';
    if (mainIndex === 1 && subIndex === 1) return 'Sinus of Valsalca';
    if (mainIndex === 1 && subIndex === 2) return 'Sinotubular Junction';
    if (mainIndex === 2 && subIndex === 0) return 'LM height';
    if (mainIndex === 2 && subIndex === 1) return 'RCA height';
    if (mainIndex === 2 && subIndex === 2) return 'STJ height';
    return null;
  }, [analysisType, mainIndex, subIndex]);

  const taviRelativeLabelPoints = useMemo(() => {
    if (analysisType !== 'tavi' || mainIndex !== 1) return null;
    const parsed = (pool as Record<string, any> | undefined)?.['tavi.parsed']
      ?.data as Record<string, any> | undefined;
    if (!parsed) return null;

    const keyByPanel =
      subIndex === 0
        ? 'annulus'
        : subIndex === 1
          ? 'sinus'
          : subIndex === 2
            ? 'sinotubularjunction'
            : null;
    if (!keyByPanel) return null;

    const raw = parsed?.[keyByPanel]?.labelPoints as unknown;
    if (!Array.isArray(raw)) return null;

    const points = raw
      .filter(
        (p): p is [number, number] =>
          Array.isArray(p) &&
          p.length >= 2 &&
          Number.isFinite(p[0]) &&
          Number.isFinite(p[1]),
      )
      .map(([x, y]) => [Number(x), Number(y)] as [number, number]);
    return points.length >= 2 ? points : null;
  }, [analysisType, mainIndex, subIndex, pool]);

  const taviRelativeExtraPoints = useMemo(() => {
    if (analysisType !== 'tavi' || mainIndex !== 1) return null;
    const parsed = (pool as Record<string, any> | undefined)?.['tavi.parsed']
      ?.data as Record<string, any> | undefined;
    if (!parsed) return null;

    const keyByPanel =
      subIndex === 0
        ? 'annulus'
        : subIndex === 1
          ? 'sinus'
          : subIndex === 2
            ? 'sinotubularjunction'
            : null;
    if (!keyByPanel) return null;

    const raw = parsed?.[keyByPanel]?.extraPoints as unknown;
    if (!Array.isArray(raw)) return null;
    const points = raw
      .filter(
        (p): p is [number, number] =>
          Array.isArray(p) &&
          p.length >= 2 &&
          Number.isFinite(p[0]) &&
          Number.isFinite(p[1]),
      )
      .map(([x, y]) => [Number(x), Number(y)] as [number, number]);
    return points.length >= 2 ? points : null;
  }, [analysisType, mainIndex, subIndex, pool]);

  const updateTaviRelativeOutlineOverlay = useCallback(() => {
    if (isAnyFullscreenActive) return;
    if (!context.current) {
      setTaviRelativeOutlineOverlay(null);
      return;
    }
    if (
      analysisType !== 'tavi' ||
      mainIndex !== 1 ||
      (!taviRelativeLabelPoints || taviRelativeLabelPoints.length < 2)
    ) {
      setTaviRelativeOutlineOverlay(null);
      setTaviRelativeExtraLinesOverlay(null);
      return;
    }

    const { renderer, renderWindow, canvas, CSReslice, actorIds } = context.current;
    if (!renderer || !renderWindow || !canvas || !CSReslice) {
      setTaviRelativeOutlineOverlay(null);
      setTaviRelativeExtraLinesOverlay(null);
      return;
    }

    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) {
      setTaviRelativeOutlineOverlay(null);
      setTaviRelativeExtraLinesOverlay(null);
      return;
    }
    const sx = canvas.width / bounds.width;
    const sy = canvas.height / bounds.height;
    const openGLRenderWindow = renderWindow.getViews()[0] as vtkOpenGLRenderWindow;

    const outputExtent = CSReslice.getOutputExtent() as [
      number,
      number,
      number,
      number,
      number,
      number,
    ];
    if (!outputExtent || outputExtent.length < 6) {
      setTaviRelativeOutlineOverlay(null);
      setTaviRelativeExtraLinesOverlay(null);
      return;
    }
    const csActor = actorIds['CSActor'] as vtkImageSlice | undefined;
    const actorBounds = csActor?.getBounds?.();
    if (!csActor || !actorBounds) {
      setTaviRelativeOutlineOverlay(null);
      setTaviRelativeExtraLinesOverlay(null);
      return;
    }

    const centerI = (outputExtent[0] + outputExtent[1]) / 2;
    const centerJ = (outputExtent[2] + outputExtent[3]) / 2;
    const lowRate = config.generalSetting.renderingQuality !== 'high' ? 0.5 : 1;

    const toCssFromDisplay = (x: number, y: number): [number, number] => [
      x / sx,
      bounds.height - y / sy,
    ];
    const worldCorners: [number, number, number][] = [
      [actorBounds[0], actorBounds[2], actorBounds[4]], // c00: xmin,ymin
      [actorBounds[1], actorBounds[2], actorBounds[4]], // c10: xmax,ymin
      [actorBounds[1], actorBounds[3], actorBounds[4]], // c11: xmax,ymax
      [actorBounds[0], actorBounds[3], actorBounds[4]], // c01: xmin,ymax
    ];
    const displayCorners = worldCorners.map((corner) => {
      const d = openGLRenderWindow.worldToDisplay(
        corner[0],
        corner[1],
        corner[2],
        renderer,
      ) as [number, number, number];
      return [d[0], d[1]] as [number, number];
    });
    const [c00, c10, c11, c01] = displayCorners;

    const xDen = Math.max(1e-6, outputExtent[1] - outputExtent[0]);
    const yDen = Math.max(1e-6, outputExtent[3] - outputExtent[2]);

    const cssPoints = taviRelativeLabelPoints.map(([rx, ry]) => {
      // Server now provides relative label points in (x, y) order.
      const i = centerI + rx * lowRate;
      const j = centerJ + ry * lowRate;

      const u = (i - outputExtent[0]) / xDen;
      const v = (j - outputExtent[2]) / yDen;

      // Bilinear interpolation on displayed actor quad
      const dx =
        c00[0] * (1 - u) * (1 - v) +
        c10[0] * u * (1 - v) +
        c11[0] * u * v +
        c01[0] * (1 - u) * v;
      const dy =
        c00[1] * (1 - u) * (1 - v) +
        c10[1] * u * (1 - v) +
        c11[1] * u * v +
        c01[1] * (1 - u) * v;
      return toCssFromDisplay(dx, dy);
    });
    const validCssPoints = cssPoints.filter(isFinite2DPoint);
    const toCssFromRelative = ([rx, ry]: [number, number]): [number, number] => {
      const i = centerI + rx * lowRate;
      const j = centerJ + ry * lowRate;
      const u = (i - outputExtent[0]) / xDen;
      const v = (j - outputExtent[2]) / yDen;
      const dx =
        c00[0] * (1 - u) * (1 - v) +
        c10[0] * u * (1 - v) +
        c11[0] * u * v +
        c01[0] * (1 - u) * v;
      const dy =
        c00[1] * (1 - u) * (1 - v) +
        c10[1] * u * (1 - v) +
        c11[1] * u * v +
        c01[1] * (1 - u) * v;
      return toCssFromDisplay(dx, dy);
    };
    const extraCssPoints = (taviRelativeExtraPoints ?? []).map(toCssFromRelative);
    const validExtraCssPoints = extraCssPoints.filter(isFinite2DPoint);
    const makeLine = (a: number, b: number): [[number, number], [number, number]] | null => {
      if (
        a < 0 ||
        b < 0 ||
        a >= validExtraCssPoints.length ||
        b >= validExtraCssPoints.length
      ) {
        return null;
      }
      return [validExtraCssPoints[a], validExtraCssPoints[b]];
    };
    let linePairs: Array<[[number, number], [number, number]]> = [];
    if (subIndex === 1) {
      // sinus: 0-1 / 2-3 / 4-5, and hide outline
      linePairs = [makeLine(0, 1), makeLine(2, 3), makeLine(4, 5)].filter(
        (v): v is [[number, number], [number, number]] => !!v,
      );
      setTaviRelativeOutlineOverlay(null);
      setTaviRelativeExtraLinesOverlay(linePairs.length > 0 ? linePairs : null);
      return;
    }
    // annulus/junction: keep outline + 0-1 / 2-3
    linePairs = [makeLine(0, 1), makeLine(2, 3)].filter(
      (v): v is [[number, number], [number, number]] => !!v,
    );
    setTaviRelativeExtraLinesOverlay(linePairs.length > 0 ? linePairs : null);
    if (validCssPoints.length < 2) {
      setTaviRelativeOutlineOverlay(null);
      return;
    }

    const first = validCssPoints[0];
    const last = validCssPoints[validCssPoints.length - 1];
    const closed =
      Math.abs(first[0] - last[0]) < 0.001 && Math.abs(first[1] - last[1]) < 0.001
        ? validCssPoints
        : [...validCssPoints, first];

    setTaviRelativeOutlineOverlay(closed);
  }, [
    isAnyFullscreenActive,
    analysisType,
    mainIndex,
    subIndex,
    taviRelativeLabelPoints,
    taviRelativeExtraPoints,
  ]);

  const updateTaviHeightLineOverlay = useCallback(() => {
    if (isAnyFullscreenActive) return;
    if (!context.current) {
      setTaviHeightLineOverlay(null);
      return;
    }
    if (analysisType !== 'tavi' || mainIndex !== 2 || (subIndex < 0 || subIndex > 2)) {
      setTaviHeightLineOverlay(null);
      return;
    }

    const { renderer, renderWindow, canvas, CSReslice, actorIds } =
      context.current;
    if (!renderer || !renderWindow || !canvas || !CSReslice) {
      setTaviHeightLineOverlay(null);
      return;
    }

    const parsed = (pool as Record<string, any> | undefined)?.['tavi.parsed']
      ?.data as Record<string, any> | undefined;
    if (!parsed) {
      setTaviHeightLineOverlay(null);
      return;
    }

    const heightPlaneRaw = parsed?.heightPlane as
      | {
          lca?: {
            ostium2d?: [number, number];
            projected2d?: [number, number];
            cusp2d?: [number, number];
          };
          rca?: {
            ostium2d?: [number, number];
            projected2d?: [number, number];
            cusp2d?: [number, number];
          };
          stj?: {
            ostium2d?: [number, number];
            projected2d?: [number, number];
            cusp2d?: [number, number];
          };
        }
      | Array<{
          ostium2d?: [number, number];
          projected2d?: [number, number];
          cusp2d?: [number, number];
        }>
      | undefined;
    const row = Array.isArray(heightPlaneRaw)
      ? heightPlaneRaw[subIndex]
      : subIndex === 0
        ? heightPlaneRaw?.lca
        : subIndex === 1
          ? heightPlaneRaw?.rca
          : heightPlaneRaw?.stj;
    const ostium2d = row?.ostium2d;
    const projected2d = row?.projected2d;
    const cusp2d = row?.cusp2d;
    const isFinite2 = (p: unknown): p is [number, number] =>
      Array.isArray(p) &&
      p.length >= 2 &&
      Number.isFinite(p[0]) &&
      Number.isFinite(p[1]);
    if (!isFinite2(ostium2d) || !isFinite2(projected2d) || !isFinite2(cusp2d)) {
      setTaviHeightLineOverlay(null);
      return;
    }
    const outputExtent = CSReslice.getOutputExtent() as [
      number,
      number,
      number,
      number,
      number,
      number,
    ];
    if (!outputExtent || outputExtent.length < 6) {
      setTaviHeightLineOverlay(null);
      return;
    }

    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) {
      setTaviHeightLineOverlay(null);
      return;
    }
    const sx = canvas.width / bounds.width;
    const sy = canvas.height / bounds.height;
    const openGLRenderWindow = renderWindow.getViews()[0] as vtkOpenGLRenderWindow;

    const csActor = actorIds['CSActor'] as vtkImageSlice | undefined;
    const actorBounds = csActor?.getBounds?.();
    if (!actorBounds) {
      setTaviHeightLineOverlay(null);
      return;
    }
    const worldCorners: [number, number, number][] = [
      [actorBounds[0], actorBounds[2], actorBounds[4]],
      [actorBounds[1], actorBounds[2], actorBounds[4]],
      [actorBounds[1], actorBounds[3], actorBounds[4]],
      [actorBounds[0], actorBounds[3], actorBounds[4]],
    ];
    const displayCorners = worldCorners.map((corner) => {
      const dxy = openGLRenderWindow.worldToDisplay(
        corner[0],
        corner[1],
        corner[2],
        renderer,
      ) as [number, number, number];
      return [dxy[0], dxy[1]] as [number, number];
    });
    const [c00, c10, c11, c01] = displayCorners;
    const xDen = Math.max(1e-6, outputExtent[1] - outputExtent[0]);
    const yDen = Math.max(1e-6, outputExtent[3] - outputExtent[2]);
    const centerI = (outputExtent[0] + outputExtent[1]) / 2;
    const centerJ = (outputExtent[2] + outputExtent[3]) / 2;
    const lowRate = config.generalSetting.renderingQuality !== 'high' ? 0.5 : 1;
    const relativeToCss = (
      pt: [number, number],
      swapXY: boolean = false,
    ): [number, number] => {
      const rx = swapXY ? pt[1] : pt[0];
      const ry = swapXY ? pt[0] : pt[1];
      const i = centerI + rx * lowRate;
      const j = centerJ + ry * lowRate;
      const uu = (i - outputExtent[0]) / xDen;
      const vv = (j - outputExtent[2]) / yDen;
      const dx =
        c00[0] * (1 - uu) * (1 - vv) +
        c10[0] * uu * (1 - vv) +
        c11[0] * uu * vv +
        c01[0] * (1 - uu) * vv;
      const dy =
        c00[1] * (1 - uu) * (1 - vv) +
        c10[1] * uu * (1 - vv) +
        c11[1] * uu * vv +
        c01[1] * (1 - uu) * vv;
      return [dx / sx, bounds.height - dy / sy];
    };

    // Server provides heightPlane 2D points in (x, y)
    const ostium = relativeToCss(ostium2d, false);
    const foot = relativeToCss(projected2d, false);
    const cusp = relativeToCss(cusp2d, false);
    const vx = cusp[0] - foot[0];
    const vy = cusp[1] - foot[1];
    const vlen = Math.hypot(vx, vy);
    if (!Number.isFinite(vlen) || vlen < 1e-6) {
      setTaviHeightLineOverlay(null);
      return;
    }
    const ux = vx / vlen;
    const uy = vy / vlen;
    const annulusExtend = Math.max(bounds.width, bounds.height) * 0.65;
    const lineA: [number, number] = [foot[0] - ux * annulusExtend, foot[1] - uy * annulusExtend];
    const lineB: [number, number] = [foot[0] + ux * annulusExtend, foot[1] + uy * annulusExtend];

    if (
      !isFinite2DPoint(lineA) ||
      !isFinite2DPoint(lineB) ||
      !isFinite2DPoint(foot) ||
      !isFinite2DPoint(ostium)
    ) {
      setTaviHeightLineOverlay(null);
      return;
    }

    setTaviHeightLineOverlay({
      annulusLine: [lineA, lineB],
      connector: [ostium, foot],
      ostium,
      projected: foot,
      cusp,
    });
  }, [
    isAnyFullscreenActive,
    analysisType,
    mainIndex,
    subIndex,
    pool,
    config.generalSetting.renderingQuality,
  ]);

  const scheduleTaviRelativeOutlineOverlayUpdate = useCallback(() => {
    requestAnimationFrame(() => {
      updateTaviRelativeOutlineOverlay();
      requestAnimationFrame(() => {
        updateTaviRelativeOutlineOverlay();
      });
    });
  }, [updateTaviRelativeOutlineOverlay]);

  const upsertAxisActor = useCallback(
    (
      key: string,
      p1: [number, number, number],
      p2: [number, number, number],
      color: [number, number, number],
    ) => {
      if (!context.current) return;
      const { actorIds, renderer } = context.current;
      let axisActor = actorIds[key] as vtkActor | undefined;

      const points = vtkPoints.newInstance();
      points.insertNextPoint(p1[0], p1[1], p1[2]);
      points.insertNextPoint(p2[0], p2[1], p2[2]);
      const lines = vtkCellArray.newInstance();
      lines.insertNextCell([0, 1]);
      const polyData = vtkPolyData.newInstance();
      polyData.setPoints(points);
      polyData.setLines(lines);

      if (!axisActor) {
        const mapper = vtkMapper.newInstance();
        mapper.setInputData(polyData);
        axisActor = vtkActor.newInstance();
        axisActor.setMapper(mapper);
        axisActor.getProperty().setLineWidth(1.5);
        axisActor.getProperty().setColor(color);
        actorIds[key] = axisActor;
        renderer.addActor(axisActor);
      } else {
        const mapper = axisActor.getMapper() as vtkMapper;
        mapper.setInputData(polyData);
      }
    },
    [],
  );

  const refreshOutlineMeasurements = useCallback(() => {
    if (!context.current) return;
    if (!shouldShowOutlineFeatures) {
      setAxisOverlay(null);
      setOutlineMetrics(null);
      return;
    }
    const { actorIds, renderWindow, renderer, canvas } = context.current;
    const setAxisVisible = (visible: boolean) => {
      const longAxis = actorIds['allLabelLongAxis'] as vtkActor;
      const shortAxis = actorIds['allLabelShortAxis'] as vtkActor;
      if (longAxis) longAxis.setVisibility(visible);
      if (shortAxis) shortAxis.setVisibility(visible);
    };
    const outlineActor = actorIds['allLabelOutlineTest'] as vtkActor;
    if (!outlineActor) {
      setAxisVisible(false);
      setAxisOverlay(null);
      setOutlineMetrics(null);
      return;
    }
    const mapper = outlineActor.getMapper() as vtkMapper;
    const input = mapper?.getInputData() as vtkPolyData;
    const raw = input?.getPoints()?.getData?.() as Float32Array | undefined;
    if (!raw || raw.length < 9) {
      setAxisVisible(false);
      setAxisOverlay(null);
      setOutlineMetrics(null);
      return;
    }

    const pos = outlineActor.getPosition() as [number, number, number];
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < raw.length; i += 3) {
      pts.push([raw[i] + pos[0], raw[i + 1] + pos[1]]);
    }
    if (pts.length < 3) {
      setAxisVisible(false);
      setAxisOverlay(null);
      setOutlineMetrics(null);
      return;
    }

    let cx = 0;
    let cy = 0;
    for (const [x, y] of pts) {
      cx += x;
      cy += y;
    }
    cx /= pts.length;
    cy /= pts.length;

    let xx = 0;
    let xy = 0;
    let yy = 0;
    for (const [x, y] of pts) {
      const dx = x - cx;
      const dy = y - cy;
      xx += dx * dx;
      xy += dx * dy;
      yy += dy * dy;
    }
    const theta = 0.5 * Math.atan2(2 * xy, xx - yy);
    const ux: [number, number] = [Math.cos(theta), Math.sin(theta)];
    const uy: [number, number] = [-ux[1], ux[0]];
    const linePolygonIntersections = (
      center: [number, number],
      dir: [number, number],
      polygon: Array<[number, number]>,
    ): number[] => {
      const ts: number[] = [];
      const eps = 1e-8;
      for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        const ex = b[0] - a[0];
        const ey = b[1] - a[1];
        const denom = dir[0] * ey - dir[1] * ex;
        if (Math.abs(denom) < eps) continue;
        const acx = a[0] - center[0];
        const acy = a[1] - center[1];
        const t = (acx * ey - acy * ex) / denom;
        const s = (acx * dir[1] - acy * dir[0]) / denom;
        if (s >= -eps && s <= 1 + eps && Number.isFinite(t)) ts.push(t);
      }
      ts.sort((a, b) => a - b);
      const unique: number[] = [];
      for (const t of ts) {
        if (unique.length === 0 || Math.abs(unique[unique.length - 1] - t) > 1e-4) {
          unique.push(t);
        }
      }
      return unique;
    };

    const center: [number, number] = [cx, cy];
    const longTs = linePolygonIntersections(center, ux, pts);
    const shortTs = linePolygonIntersections(center, uy, pts);

    const makeSeg = (dir: [number, number], ts: number[]) => {
      if (ts.length >= 2) {
        const t0 = ts[0];
        const t1 = ts[ts.length - 1];
        return {
          p1: [center[0] + dir[0] * t0, center[1] + dir[1] * t0, 0] as [number, number, number],
          p2: [center[0] + dir[0] * t1, center[1] + dir[1] * t1, 0] as [number, number, number],
          len: Math.abs(t1 - t0),
        };
      }
      // fallback: projection span
      let minT = Number.POSITIVE_INFINITY;
      let maxT = Number.NEGATIVE_INFINITY;
      for (const [x, y] of pts) {
        const t = (x - center[0]) * dir[0] + (y - center[1]) * dir[1];
        if (t < minT) minT = t;
        if (t > maxT) maxT = t;
      }
      return {
        p1: [center[0] + dir[0] * minT, center[1] + dir[1] * minT, 0] as [number, number, number],
        p2: [center[0] + dir[0] * maxT, center[1] + dir[1] * maxT, 0] as [number, number, number],
        len: Math.max(0, maxT - minT),
      };
    };

    const longSeg = makeSeg(ux, longTs);
    const shortSeg = makeSeg(uy, shortTs);
    const longP1 = longSeg.p1;
    const longP2 = longSeg.p2;
    const shortP1 = shortSeg.p1;
    const shortP2 = shortSeg.p2;

    upsertAxisActor('allLabelLongAxis', longP1, longP2, [1.0, 0.3, 0.3]);
    upsertAxisActor('allLabelShortAxis', shortP1, shortP2, [1.0, 0.3, 0.3]);
    setAxisVisible(true);

    // Screen-space overlay lines for clear visual feedback
    const openGLRenderWindow = renderWindow.getViews()[0] as vtkOpenGLRenderWindow;
    const bounds = canvas.getBoundingClientRect();
    const sx = bounds.width > 0 ? canvas.width / bounds.width : 1;
    const sy = bounds.height > 0 ? canvas.height / bounds.height : 1;
    const toCss = (p: [number, number, number]): [number, number] => {
      const d = openGLRenderWindow.worldToDisplay(p[0], p[1], p[2], renderer) as [
        number,
        number,
        number,
      ];
      return [d[0] / sx, bounds.height - d[1] / sy];
    };
    setAxisOverlay({
      long: [toCss(longP1), toCss(longP2)],
      short: [toCss(shortP1), toCss(shortP2)],
    });

    let area2 = 0;
    for (let i = 0; i < pts.length; i++) {
      const [x1, y1] = pts[i];
      const [x2, y2] = pts[(i + 1) % pts.length];
      area2 += x1 * y2 - x2 * y1;
    }
    const area = Math.abs(area2) * 0.5;
    const longAxis = longSeg.len;
    const shortAxis = shortSeg.len;
    const diameter = area > 0 ? 2 * Math.sqrt(area / Math.PI) : 0;
    setOutlineMetrics({ shortAxis, longAxis, area, diameter });
  }, [upsertAxisActor, shouldShowOutlineFeatures]);

  useEffect(() => {
    if (!ref.current) return;
    const currentDiv = ref.current;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.contentRect) {
          const { width, height } = entry.contentRect;
          setDimensions((prev) =>
            prev.width === width && prev.height === height
              ? prev
              : { width, height },
          );
        }
      });
    });

    if (currentDiv) {
      observer.observe(currentDiv);
    }

    return () => {
      if (currentDiv) {
        observer.unobserve(currentDiv);
      }
    };
  }, []);

  useEffect(() => {
    if (shouldUseCrossSectionImageShell || shouldUseServerCrossSectionCanvas) {
      const existing = contextPool[rendererId as ContextKey];
      if (existing) {
        const existingRef = {
          current: existing,
        } as React.MutableRefObject<CrossSectionContext | null>;
        cleanUpVtkRenderer(existingRef, existing);
        existing.renderWindow?.delete?.();
        existing.genericRenderWindow?.delete?.();
      }
      ref.current
        ?.querySelectorAll('canvas')
        .forEach((canvas) => {
          if (!(canvas as HTMLCanvasElement).dataset.serverCrossSectionRaster) {
            canvas.remove();
          }
        });
      setContextPool((prev) => ({ ...prev, [rendererId]: null }));
      (context as React.MutableRefObject<CrossSectionContext | null>).current =
        null;
      return;
    }
    if (!ref.current) return;

    let cntx = contextPool[rendererId as ContextKey];
    if (!cntx) {
      cntx = initializeVtkRenderer(rendererId, 'CrossSection');
      setContextPool((prev) => ({ ...prev, [rendererId]: cntx }));
    }

    if (!cntx) {
      throw new Error(`Failed to initialize CrossSection renderer: ${rendererId}`);
    }
    setContainer(cntx, ref);
    (context as React.MutableRefObject<CrossSectionContext>).current =
      cntx as CrossSectionContext;
    return () => {
      cleanUpVtkRenderer(context, cntx);
      setContextPool((prev) => ({ ...prev, [rendererId]: cntx }));
    };
  }, [
    rendererId,
    analysisType,
    shouldUseCrossSectionImageShell,
    shouldUseServerCrossSectionCanvas,
    effectiveSourceSubIndex,
  ]);

  useEffect(() => {
    if (shouldUseCrossSectionImageShell || shouldUseServerCrossSectionCanvas) return;
    if (!context.current) return;
    if (!selectedSeriesId) return;
    if (!isDataReady) return;
    if (!branchIds || branchId === undefined) return;
    // if (analysisType === 'aorticValveCalcium') return;

    const { renderingQuality } = config.generalSetting;
    const HUVolume = getVolumeFromSeriesId(
      selectedSeriesId,
      renderingQuality !== 'high',
    );
    if (!HUVolume) return;

    const labelVolume = getLabelVolumeFromSeriesId(
      selectedSeriesId,
      renderingQuality !== 'high',
    );
    if (!labelVolume) return;

    const label = isAorticMode
      ? 'Aorta'
      : (Object.entries(
          branchIds.find((obj) =>
            Object.prototype.hasOwnProperty.call(obj, branchId),
          ) ?? {},
        )[0]?.[1] as string | undefined);

    //console.log(branchId, subIndex, mainIndex, label);

    if (!isAorticMode && !label) return;
    const huData = HUVolume.imageData as vtkImageData;
    let labelData = (pool?.labelData.data as Record<number, vtkImageData>)[
      branchId
    ];

    let colorPoints: ReturnType<typeof makeCoronaryColorPoints>;
    let locOpacityPoints: ReturnType<typeof makeOpacityPoints>;

    if (label === 'Aorta' || isAorticMode) {
      labelData = labelVolume.imageData as vtkImageData;
      const colorarr: ReturnType<typeof makeCoronaryColorPoints> = [];
      for (const clr of config.aorta_colors) {
        colorarr.push(rgbToNormalizedARGB(clr));
      }
      colorPoints = colorarr;
      locOpacityPoints = config.aorta_opacitys;
    } else {
      colorPoints = makeCoronaryColorPoints(config.colors);
      locOpacityPoints = makeOpacityPoints(analysisType);
    }

    const serverCameraScopeKey = [
      selectedSeriesId ?? 'none',
      analysisType,
      branchId,
      mainIndex,
      subIndex,
    ].join(':');
    const shouldPreserveServerCamera =
      isServerFrontDataImageManifest &&
      (!isAorticMode || isAorticServerFrontDataMode) &&
      context.current.serverFrontDataVtkMode === true &&
      serverCameraScopeKeyRef.current === serverCameraScopeKey;
    const preservedServerCamera = shouldPreserveServerCamera
      ? captureCrossSectionCamera()
      : null;

    createCrossSection(
      context,
      huData,
      labelData,
      pool,
      branchId,
      currentVOI,
      analysisType,
      colorPoints,
      locOpacityPoints,
      shouldShowOutlineFeatures,
      !isAorticMode
        ? getServerFrameVectorAxes(config.coronary_front_expand, huData)
        : undefined,
    );

    const { actorIds } = context.current;
    const paintActor = actorIds['CSPaintActor'] as vtkImageSlice;
    if (!paintActor) return;
    if (
      analysisType === 'plaque'
      // || analysisType === 'stenosis'
      // || analysisType === 'isr'
    ) {
      const pwf = paintActor.getProperty().getPiecewiseFunction();
      pwf?.addPoint(251, 0);
      pwf?.addPoint(252, 0);
      pwf?.addPoint(253, 0);
      pwf?.addPoint(254, 0);
      paintActor.getProperty().setPiecewiseFunction(0, pwf);
      //renderWindow.render();
    } else if (analysisType === 'fai') {
      const pwf = paintActor.getProperty().getPiecewiseFunction();
      pwf?.addPoint(1, 0);
      pwf?.addPoint(2, 0);
      pwf?.addPoint(3, 0);
      // pwf?.addPoint(255, 0);
      paintActor.getProperty().setPiecewiseFunction(0, pwf);
      //renderWindow.render();
    } else if (analysisType === 'stenosis'){
      const pwf = paintActor.getProperty().getPiecewiseFunction();
      pwf?.addPoint(1, 0);
      pwf?.addPoint(2, 0);
      pwf?.addPoint(3, 0);
      pwf?.addPoint(251, 0);
      pwf?.addPoint(252, 0);
      pwf?.addPoint(253, 0);
      pwf?.addPoint(254, 0);
      paintActor.getProperty().setPiecewiseFunction(0, pwf);
    }

    const centerPolyData = ((context.current[
      'centerlineData'
    ] as vtkPolyData | undefined) ??
      (pool?.centerData.data as Record<number, vtkPolyData>)[branchId]) as
      | vtkPolyData
      | undefined;
    const centerMaxIndex =
      centerPolyData?.getPointData().getTensors().getNumberOfTuples?.() ?? 0;
    const middleAortaIndex =
      centerMaxIndex > 0
        ? Math.floor(centerMaxIndex / 2)
        : aorticCurrentIndex;
    const cor_index = info === undefined ? config.coronary_front_expand : info.index;
    const syncedAorticIndex =
      [
        Number(info?.index),
        Number(aorticCurrentIndex),
        Number(middleAortaIndex),
      ].find(
        (value) =>
          Number.isFinite(value) &&
          value >= 0 &&
          (centerMaxIndex <= 0 || value < centerMaxIndex),
      ) ?? middleAortaIndex;
    const taviCenterKeyBySubIndex: Record<
      number,
      'annulus' | 'sinus' | 'sinotubularjunction'
    > = {
      0: 'annulus',
      1: 'sinus',
      2: 'sinotubularjunction',
    };
    const taviCenterKey = taviCenterKeyBySubIndex[subIndex];
    const taviCenterIdxRaw =
      analysisType === 'tavi' && mainIndex === 1
        ? getTaviCenterIdx(
            pool,
            taviCenterKey ?? 'annulus',
          )
        : null;
    const taviCenterIdx =
      taviCenterIdxRaw !== null && taviCenterIdxRaw >= 0
        ? taviCenterIdxRaw
        : null;

    const targetIndex = !isAorticMode
      ? cor_index
      : analysisType === 'tavi' && mainIndex === 1
        ? (taviCenterIdx ?? middleAortaIndex)
        : isAorticServerFrontDataMode
          ? syncedAorticIndex
          : middleAortaIndex;
    const serverFrameVectorAxes =
      isServerFrontDataImageManifest &&
      (!isAorticMode || isAorticServerFrontDataMode)
        ? getServerFrameVectorAxes(targetIndex, huData)
        : undefined;

    // 한번쯤은
    updateMidCrossSection(
      context,
      pool,
      branchId,
      targetIndex,
      analysisType,
      config.upscale,
      shouldShowOutlineFeatures,
      taviPanelAxes ?? serverFrameVectorAxes,
    );
    applyFixedPanelZoom();
    if (preservedServerCamera) {
      restoreCrossSectionCamera(preservedServerCamera);
    }
    serverCameraScopeKeyRef.current = serverCameraScopeKey;
    refreshOutlineMeasurements();
    scheduleTaviRelativeOutlineOverlayUpdate();

    setNewIndex(
      isAorticMode ? targetIndex : targetIndex - config.coronary_front_expand,
    );
  }, [
    context,
    isDataReady,
    branchIds,
    branchId,
    analysisType,
    shouldShowOutlineFeatures,
    aorticCurrentIndex,
    info?.index,
    config.coronary_front_expand,
    isAorticMode,
    isAorticServerFrontDataMode,
    isServerFrontDataImageManifest,
    mainIndex,
    subIndex,
    refreshOutlineMeasurements,
    taviPanelAxes,
    (pool as Record<string, any> | undefined)?.['tavi.annulus']?.loaded,
    (pool as Record<string, any> | undefined)?.['tavi.sinus']?.loaded,
    (pool as Record<string, any> | undefined)?.['tavi.sinotubularjunction']
      ?.loaded,
    (pool as Record<string, any> | undefined)?.['tavi.centerTangent']?.loaded,
    (pool as Record<string, any> | undefined)?.['tavi.heightPlane']?.loaded,
    (pool as Record<string, any> | undefined)?.['tavi.parsed']?.loaded,
    scheduleTaviRelativeOutlineOverlayUpdate,
    applyFixedPanelZoom,
    captureCrossSectionCamera,
    restoreCrossSectionCamera,
    selectedSeriesId,
    shouldUseCrossSectionImageShell,
    getServerFrameVectorAxes,
  ]);

  useEffect(() => {
    if (shouldUseCrossSectionImageShell || shouldUseServerCrossSectionCanvas) return;
    if (!context.current) return;
    const { actorIds } = context.current;
    if (actorIds) {
      const actor = actorIds['CSActor'] as vtkImageSlice;
      if (actor && actor.getProperty()) {
        actor.getProperty().setColorLevel(windowLevel);
        actor.getProperty().setColorWindow(windowWidth);
        scheduleRender(rendererId, { type: 'updateRenderWindow' });
      }
    }
  }, [
    windowLevel,
    windowWidth,
    invert,
    rendererId,
    shouldUseCrossSectionImageShell,
    shouldUseServerCrossSectionCanvas,
  ]);

  const getCrossSectionCenterlineData = useCallback(
    () =>
      ((pool?.centerData.data as Record<number, vtkPolyData> | undefined)?.[
        branchId
      ] ?? (context.current?.['centerlineData'] as vtkPolyData | undefined)) as
        | vtkPolyData
        | undefined,
    [branchId, pool?.centerData.data],
  );

  const dispatchCrossSectionImageIndex = useCallback(
    (nextIndex: number, which: number = effectiveSourceSubIndex) => {
      if (!crossSectionImageStack) return;
      if (!Number.isFinite(nextIndex)) return;
      if (!isCrossSectionImageIndexAllowed(nextIndex)) return;
      const resolvedNextIndex = resolveCrossSectionImageIndex(nextIndex);
      if (!isCrossSectionImageIndexAllowed(resolvedNextIndex)) return;

      if (
        isLesionStackViewLayout &&
        !isAorticMode &&
        (which === 0 || which === 2)
      ) {
        const proximalIndex =
          which === 0 ? resolvedNextIndex : Number(totalInfo[0]?.index);
        const distalIndex =
          which === 2 ? resolvedNextIndex : Number(totalInfo[2]?.index);
        if (
          Number.isFinite(proximalIndex) &&
          Number.isFinite(distalIndex) &&
          proximalIndex >= distalIndex
        ) {
          return;
        }
      } else if (!isAorticMode && branchIndices.length >= 3) {
        const [left, middle, right] = branchIndices;
        if (which === 0 && resolvedNextIndex > middle) return;
        if (
          which === 1 &&
          (resolvedNextIndex < left || resolvedNextIndex > right)
        )
          return;
        if (which === 2 && resolvedNextIndex < middle) return;
      }

      const imageAsset = getCrossSectionImageAsset(
        imageManifest,
        branchId,
        resolvedNextIndex,
      );
      const centerlineData = getCrossSectionCenterlineData();
      const node = centerlineData
        ? handleNodeChange(centerlineData, resolvedNextIndex)
        : null;
      const assetPickedPoint = buildPickPointFromCrossSectionAsset(
        imageAsset,
        resolvedNextIndex,
      );
      const pickedPoint =
        node
          ? {
              position: node.position,
              normal: node.normal,
              index: resolvedNextIndex,
            }
          : assetPickedPoint;
      const hasWorldPickedPoint =
        Array.isArray((pickedPoint as any).position) &&
        Array.isArray((pickedPoint as any).normal);

      imageIndexRef.current = resolvedNextIndex;
      setImageIndex(resolvedNextIndex);
      setNewIndex(
        isAorticMode
          ? resolvedNextIndex
          : resolvedNextIndex - config.coronary_front_expand,
      );
      updateCPRState({
        indexedState: {
          mainIndex: {
            branchId,
            index: which,
          },
          pickPoint: pickedPoint,
        },
      });

      (['stretched', 'straightened'] as const).forEach((modeName) => {
        scheduleRender(`CPR.${mainIndex}.${modeName}`, {
          type: 'markerMove',
          which,
          newIndices: resolvedNextIndex,
          newPickedPoints: [pickedPoint],
        });
        scheduleRender(`MarkerLine.${mainIndex}.${modeName}`, {
          type: 'markerMove',
          index: resolvedNextIndex,
          which,
        });
      });

      if (isAorticMode) {
        setData({ aorta_current_index: resolvedNextIndex });
        if (hasWorldPickedPoint) {
          scheduleRender('Axial', {
            type: 'focus',
            world: (pickedPoint as any).position,
            mode: 'cpr',
            request: true,
          });
          scheduleRender('Surface3D', {
            type: 'aorta_node',
            world: (pickedPoint as any).position,
            normal: (pickedPoint as any).normal,
          });
        }
        return;
      }

      if (hasWorldPickedPoint) {
        scheduleRender('Axial', {
          type: 'focus',
          world: (pickedPoint as any).position,
          mode: 'cpr',
          request: true,
        });
        scheduleRender('Surface3D', {
          type: 'markerMove',
          mainIndex,
          which,
          newPickedPoints: [pickedPoint],
        });
        scheduleRender('VR', {
          type: 'markerMove',
          mainIndex,
          which,
          newPickedPoints: [pickedPoint],
        });
      }
      scheduleRender('Report', {
        type: 'markerMove',
        mainIndex,
        which,
        newIndex: resolvedNextIndex - config.coronary_front_expand,
      });
      scheduleRender(analysisType === 'fai' ? `CPR-${mainIndex}-20` : 'Graph', {
        type: 'markerMove',
        mainIndex,
        which,
        newIndex: resolvedNextIndex - config.coronary_front_expand,
      });
    },
    [
      analysisType,
      branchId,
      branchIndices,
      config.coronary_front_expand,
      crossSectionImageStack,
      getCrossSectionCenterlineData,
      imageManifest,
      isAorticMode,
      isCrossSectionImageIndexAllowed,
      isLesionStackViewLayout,
      mainIndex,
      lesionStackLengthArray,
      resolveCrossSectionImageIndex,
      setData,
      effectiveSourceSubIndex,
      totalInfo,
      updateCPRState,
    ],
  );

  const dispatchLesionStackMiddleAnchorDelta = useCallback(
    (delta: number) => {
      if (!isLesionStackMiddlePanel) return;
      if (!crossSectionImageStack) return;
      if (!Number.isFinite(delta) || delta === 0) return;

      const currentAnchorIndex =
        lesionStackCrossSectionIndexes[1] ??
        getLesionStackDefaultMiddleAnchorIndex({
          totalInfo,
          lengthArray: lesionStackLengthArray,
          frontExpand: config.coronary_front_expand,
          spacingMm: lesionStackSpacingMm,
        });
      if (!Number.isFinite(currentAnchorIndex)) return;

      const nextAnchorIndex = resolveLesionStackMiddleAnchorIndexBySpacingStep({
        totalInfo,
        lengthArray: lesionStackLengthArray,
        frontExpand: config.coronary_front_expand,
        currentAnchorFullIndex: currentAnchorIndex,
        spacingMm: lesionStackSpacingMm,
        stepCount: delta,
      });
      if (!Number.isFinite(nextAnchorIndex)) return;
      if (!isCrossSectionImageIndexAllowed(nextAnchorIndex)) return;
      if (
        !isLesionStackMiddleAnchorAllowed({
          totalInfo,
          lengthArray: lesionStackLengthArray,
          frontExpand: config.coronary_front_expand,
          middleAnchorFullIndex: nextAnchorIndex,
          spacingMm: lesionStackSpacingMm,
        })
      ) {
        return;
      }

      const imageAsset = getCrossSectionImageAsset(
        imageManifest,
        branchId,
        nextAnchorIndex,
      );
      const centerlineData = getCrossSectionCenterlineData();
      const node = centerlineData
        ? handleNodeChange(centerlineData, nextAnchorIndex)
        : null;
      const assetPickedPoint = buildPickPointFromCrossSectionAsset(
        imageAsset,
        nextAnchorIndex,
      );
      const pickedPoint = node
        ? {
            position: node.position,
            normal: node.normal,
            index: nextAnchorIndex,
          }
        : assetPickedPoint;

      const nextStackIndexes = buildLesionStackCrossSectionIndexes({
        totalInfo,
        lengthArray: lesionStackLengthArray,
        frontExpand: config.coronary_front_expand,
        middleAnchorFullIndex: nextAnchorIndex,
        spacingMm: lesionStackSpacingMm,
      });
      const nextPanelIndex = nextStackIndexes[lesionStackSlotIndex] ?? nextAnchorIndex;
      imageIndexRef.current = nextPanelIndex;
      setImageIndex(nextPanelIndex);
      setNewIndex(nextPanelIndex - config.coronary_front_expand);

      updateCPRState({
        indexedState: {
          mainIndex: {
            branchId,
            index: 1,
          },
          pickPoint: pickedPoint,
        },
      });

      for (let slot = 1; slot <= PLAQUE_CPR_STACK_MIDDLE_COUNT; slot += 1) {
        const slotIndex = nextStackIndexes[slot];
        if (!Number.isFinite(slotIndex)) continue;
        scheduleRender(getLesionStackCrossSectionRendererId(mainIndex, slot), {
          type: 'markerMove',
          which: 1,
          newIndex: slotIndex,
        });
      }
      scheduleRender('Graph', {
        type: 'markerMove',
        mainIndex,
        which: 1,
        newIndex: nextAnchorIndex - config.coronary_front_expand,
      });
    },
    [
      branchId,
      config.coronary_front_expand,
      crossSectionImageStack,
      getCrossSectionCenterlineData,
      imageManifest,
      isCrossSectionImageIndexAllowed,
      isLesionStackMiddlePanel,
      mainIndex,
      lesionStackCrossSectionIndexes,
      lesionStackLengthArray,
      lesionStackSlotIndex,
      lesionStackSpacingMm,
      totalInfo,
      updateCPRState,
    ],
  );

  const handleCrossSectionImageWheel = useCallback(
    (event: ImageLayerStackWheelEvent) => {
      if (event.cancelable) event.preventDefault();
      const delta = event.deltaY > 0 ? 1 : -1;
      if (isLesionStackMiddlePanel) {
        dispatchLesionStackMiddleAnchorDelta(delta);
        return;
      }
      dispatchCrossSectionImageIndex(imageIndex + delta, effectiveSourceSubIndex);
    },
    [
      dispatchCrossSectionImageIndex,
      dispatchLesionStackMiddleAnchorDelta,
      effectiveSourceSubIndex,
      imageIndex,
      isLesionStackMiddlePanel,
    ],
  );

  const handleCrossSectionImageScrollDrag = useCallback(
    (_deltaX: number, deltaY: number) => {
      if (deltaY === 0) return;
      if (!crossSectionImageStack) return;
      const delta = deltaY > 0 ? 1 : -1;
      if (isLesionStackMiddlePanel) {
        dispatchLesionStackMiddleAnchorDelta(delta);
        return;
      }
      const nextIndex = imageIndexRef.current + delta;
      if (!isCrossSectionImageIndexAllowed(nextIndex)) return;
      imageIndexRef.current = nextIndex;
      dispatchCrossSectionImageIndex(nextIndex, effectiveSourceSubIndex);
    },
    [
      crossSectionImageStack,
      dispatchCrossSectionImageIndex,
      dispatchLesionStackMiddleAnchorDelta,
      effectiveSourceSubIndex,
      isCrossSectionImageIndexAllowed,
      isLesionStackMiddlePanel,
    ],
  );

  const handleServerCrossSectionCanvasWheelIndex = useCallback(
    (delta: number) => {
      if (!Number.isFinite(delta) || delta === 0) return;
      const safeDelta = delta > 0 ? Math.ceil(delta) : Math.floor(delta);
      if (isLesionStackMiddlePanel) {
        dispatchLesionStackMiddleAnchorDelta(safeDelta > 0 ? 1 : -1);
        return;
      }
      dispatchCrossSectionImageIndex(imageIndexRef.current + safeDelta, effectiveSourceSubIndex);
    },
    [
      dispatchCrossSectionImageIndex,
      dispatchLesionStackMiddleAnchorDelta,
      effectiveSourceSubIndex,
      isLesionStackMiddlePanel,
    ],
  );

  const applyCrossSectionImageIndexFromCprMarker = useCallback(
    (
      nextIndex: number,
      reason: 'markerMove' | 'markerMoveDone' | 'aorticCalcium_move',
      sentAt?: number,
      markerGroupPreview = false,
    ) => {
      if (!crossSectionImageStack) return;
      if (!Number.isFinite(nextIndex)) return;

      if (!isCrossSectionImageIndexAllowed(nextIndex)) return;
      const resolvedNextIndex = resolveCrossSectionImageIndex(nextIndex);
      if (!isCrossSectionImageIndexAllowed(resolvedNextIndex)) return;

      if (cprMarkerPreviewResetTimerRef.current !== null) {
        window.clearTimeout(cprMarkerPreviewResetTimerRef.current);
        cprMarkerPreviewResetTimerRef.current = null;
      }
      if (reason === 'markerMove') {
        const nextPreviewMode = markerGroupPreview ? 'group' : 'single';
        if (cprMarkerPreviewModeRef.current !== nextPreviewMode) {
          cprMarkerPreviewModeRef.current = nextPreviewMode;
          setCprMarkerPreviewMode(nextPreviewMode);
        }
        cprMarkerPreviewResetTimerRef.current = window.setTimeout(() => {
          cprMarkerPreviewResetTimerRef.current = null;
          if (cprMarkerPreviewModeRef.current !== 'none') {
            cprMarkerPreviewModeRef.current = 'none';
            setCprMarkerPreviewMode('none');
          }
        }, 180);
      } else if (cprMarkerPreviewModeRef.current !== 'none') {
        cprMarkerPreviewModeRef.current = 'none';
        setCprMarkerPreviewMode('none');
      }

      const applyLocalIndexState = () => {
        // CPR marker drag already owns the global CPR state update. Keep this
        // path local so Cross-section does not immediately send another sync
        // loop back to CPR / 3D / VR on every pointer move.
        if (imageIndexRef.current !== resolvedNextIndex) {
          imageIndexRef.current = resolvedNextIndex;
          setImageIndex(resolvedNextIndex);
        }

        if (!(reason === 'markerMove' && markerGroupPreview)) {
          const nextDisplayIndex = isAorticMode
            ? resolvedNextIndex
            : resolvedNextIndex - config.coronary_front_expand;
          setNewIndex((prev) =>
            prev === nextDisplayIndex ? prev : nextDisplayIndex,
          );
        }
      };

      if (reason === 'markerMove' && markerGroupPreview) {
        startTransition(applyLocalIndexState);
      } else {
        applyLocalIndexState();
      }
    },
    [
      config.coronary_front_expand,
      crossSectionImageStack,
      isAorticMode,
      isCrossSectionImageIndexAllowed,
      resolveCrossSectionImageIndex,
      subIndex,
    ],
  );

  useLayoutEffect(() => {
    if (!rendererId) return;

    if (shouldUseCrossSectionImageShell || shouldUseServerCrossSectionCanvas) {
      const rendererEntry = {
        render: (args?: any) => {
          const { type } = args ?? {};
          switch (type) {
            case 'reset': {
              const nextIndex =
                isLesionStackViewLayout && resolvedStackCrossSectionIndex !== undefined
                  ? resolvedStackCrossSectionIndex
                  : getDefaultCrossSectionImageIndex();
              imageIndexRef.current = nextIndex;
              setImageIndex(nextIndex);
              setNewIndex(
                isAorticMode ? nextIndex : nextIndex - config.coronary_front_expand,
              );
              break;
            }
            case 'markerMove':
            case 'markerMoveDone':
            case 'aorticCalcium_move': {
              const nextIndex = Number(args?.newIndex);
              if (!Number.isFinite(nextIndex)) break;
              const sentAt = Number(args?.sentAt);
              applyCrossSectionImageIndexFromCprMarker(
                nextIndex,
                type,
                Number.isFinite(sentAt) ? sentAt : undefined,
                shouldUseServerCrossSectionCanvas
                  ? false
                  : args?.markerGroupPreview === true,
              );
              break;
            }
            default:
              break;
          }
        },
      };

      return registerRenderer(rendererId, rendererEntry);
    }

    const rendererEntry = {
      render: (args?: any) => {
        const { type } = args ?? {};
        if (!context.current) return;
        const { actorIds } = context.current;

        switch (type) {
          case 'reset': {
            context.current?.renderer.resetCamera();
            applyFixedPanelZoom(true);
            context.current?.genericRenderWindow?.resize();
            context.current?.renderWindow.render();
            break;
          }
          case 'markerMoveDone':
          case 'markerMove':
          case 'aorticCalcium_move': {
            if (isTaviFixedPanel || !pool) break;
            const { newIndex, which } = args ?? {};
            if (!Number.isFinite(newIndex)) break;
            const cprMarkerIndex =
              Number.isFinite(which)
                ? Number(which)
                : isAorticServerFrontDataMode
                  ? 0
                  : null;
            const serverFrameVectorAxes =
              isServerFrontDataImageManifest &&
              (!isAorticMode || isAorticServerFrontDataMode)
                ? getServerFrameVectorAxes(
                    newIndex,
                    context.current?.["imageData"] as vtkImageData | undefined,
                  )
                : undefined;
            const isFinalMarkerCommit =
              type === 'markerMoveDone' || type === 'aorticCalcium_move';

            updateMidCrossSection(
              context,
              pool,
              branchId,
              newIndex,
              analysisType,
              config.upscale,
              shouldShowOutlineFeatures,
              serverFrameVectorAxes,
            );

            if (shouldShowOutlineFeatures) {
              refreshOutlineMeasurements();
            } else {
              setAxisOverlay(null);
              setOutlineMetrics(null);
            }

            scheduleTaviRelativeOutlineOverlayUpdate();
            if (
              isFinalMarkerCommit &&
              cprMarkerIndex !== null &&
              !isAorticMode
            ) {
              updateCPRState({
                indexedState: {
                  mainIndex: {
                    branchId,
                    index: cprMarkerIndex,
                  },
                  pickPoint: { index: newIndex },
                },
              });
            }

            setNewIndex(
              isAorticMode
                ? newIndex
                : newIndex - config.coronary_front_expand,
            );

            if (!isAorticMode) break;
            const centerlineData = (context.current?.[
              'centerlineData'
            ] as vtkPolyData | undefined) ??
              (pool.centerData.data as Record<number, vtkPolyData>)[branchId];
            if (!centerlineData) break;

            const taviNormalOverride =
              analysisType === 'tavi'
                ? getTaviCenterTangentAtIndex(
                    pool,
                    newIndex,
                    context.current?.['imageData'] as vtkImageData | undefined,
                  )
                : null;
            const { world, normal } = updataAorticNode(
              context,
              centerlineData as vtkPolyData,
              newIndex,
              taviNormalOverride,
            );
            if (cprMarkerIndex !== null) {
              updateCPRState({
                indexedState: {
                  mainIndex: {
                    branchId,
                    index: cprMarkerIndex,
                  },
                  pickPoint: { index: newIndex, position: world, normal },
                },
              });
            }
            if (isAorticServerFrontDataMode) {
              setData({ aorta_current_index: newIndex });
            }
            scheduleRender('Surface3D', { type: 'aorta_node', world, normal });
            break;
          }
          case 'updateRenderWindow': {
            if (!context.current) return;
            if (isAorticMode) {
              labelorlineonoff(
                crossSectionAnalysisType,
                contextPool,
                'aorta',
                false,
                crossSectionOverlayLabel.aorta !== false,
                'crossSection',
              );
              labelorlineonoff(
                crossSectionAnalysisType,
                contextPool,
                'avc',
                false,
                crossSectionOverlayLabel.avc !== false,
                'crossSection',
              );
            } else {
              const forcedLabelVisibility = isLesionStackViewLayout
                ? false
                : undefined;
              labelorlineonoff(
                crossSectionAnalysisType,
                contextPool,
                'lumen',
                false,
                forcedLabelVisibility ?? crossSectionOverlayLabel.lumen,
                'crossSection',
              );
              labelorlineonoff(
                crossSectionAnalysisType,
                contextPool,
                'lumenoutline',
                false,
                forcedLabelVisibility ?? crossSectionOverlayLabel.lumenoutline,
                'crossSection',
              );
              if (analysisType === 'plaque') {
                labelorlineonoff(
                  crossSectionAnalysisType,
                  contextPool,
                  'plaque',
                  false,
                  forcedLabelVisibility ?? crossSectionOverlayLabel.plaque,
                  'crossSection',
                );
                labelorlineonoff(
                  crossSectionAnalysisType,
                  contextPool,
                  'plaqueoutline',
                  false,
                  forcedLabelVisibility ?? crossSectionOverlayLabel.plaqueoutline,
                  'crossSection',
                );
              }
              if (analysisType === 'fai') {
                labelorlineonoff(
                  crossSectionAnalysisType,
                  contextPool,
                  'fai',
                  false,
                  forcedLabelVisibility ?? crossSectionOverlayLabel.fai,
                  'crossSection',
                );
              }
            }
            updateRenderWindow(context);
            applyFixedPanelZoom(true);
            break;
          }
          case 'colorchange': {
            const { target, newSegmentation } = args ?? {};
            if (actorIds) {
              const actor = actorIds['CSPaintActor'] as vtkImageSlice;
              if (
                colorandopacitycontrol2D(
                  analysisType,
                  target,
                  newSegmentation,
                  actor,
                )
              ) {
                updateRenderWindow(context);
              }
            }
            break;
          }
          default:
            break;
        }
      },
    };

    return registerRenderer(rendererId, rendererEntry);
  });

  const metricOverlay = useMemo(
    () =>
      getCrossSectionMetricOverlay({
        analysisType,
        branchGraphDataset: hasBranchGraphDataset ? branchGraphDataset : null,
        branchId,
        index: newIndex,
      }),
    [
      analysisType,
      branchGraphDataset,
      branchId,
      hasBranchGraphDataset,
      newIndex,
    ],
  );


  const serverCanvasResources = useMemo(() => {
    if (!shouldUseServerCrossSectionCanvas || !selectedSeriesId || !pool) {
      return null;
    }
    const { renderingQuality } = config.generalSetting;
    const HUVolume = getVolumeFromSeriesId(
      selectedSeriesId,
      renderingQuality !== 'high',
    );
    if (!HUVolume?.imageData) return null;
    const labelVolume = getLabelVolumeFromSeriesId(
      selectedSeriesId,
      renderingQuality !== 'high',
    );
    let localLabelData = (pool?.labelData.data as Record<number, vtkImageData> | undefined)?.[
      branchId
    ];
    if (isAorticMode) {
      localLabelData = labelVolume?.imageData as vtkImageData | undefined;
    }
    if (!localLabelData) return null;

    const rate = renderingQuality !== 'high' ? 0.5 : 1;
    const localBaseWidth = Math.max(
      8,
      Math.floor(
        (isAorticMode ? config.aorta_basewidth : config.basewidth) * rate,
      ),
    );
    const localUpscale = isAorticMode ? 0 : config.upscale;
    const localAxes = getServerFrameVectorAxes(
      imageIndex,
      HUVolume.imageData as vtkImageData,
    );

    return {
      imageData: HUVolume.imageData as vtkImageData,
      labelData: localLabelData,
      axes: localAxes,
      baseWidth: localBaseWidth,
      upscale: localUpscale,
    };
  }, [
    branchId,
    config.aorta_basewidth,
    config.basewidth,
    config.generalSetting,
    config.upscale,
    getServerFrameVectorAxes,
    imageIndex,
    isAorticMode,
    pool,
    selectedSeriesId,
    shouldUseServerCrossSectionCanvas,
  ]);

  const renderBottomRightContent = useMemo(() => {
    if (!metricOverlay) return null;

    const fmt = (value?: number | null) => value?.toFixed(2) ?? '--';
    const compactFmt = (value?: number | null) => value?.toFixed(2) ?? '--';

    const metricLine = (label: string, value: number | null | undefined, unit: string) => (
      <div className="metric-line" key={label}>{`${label} ${fmt(value)} ${unit}`}</div>
    );
    const metricSingleValue = (value: number | null | undefined, unit: string) => (
      <div className="metric-line metric-line-single" key="single-value">{`${fmt(value)} ${unit}`}</div>
    );
    const metricGroup = (
      title: string,
      kind: 'wall' | 'lumen' | 'length',
      children: React.ReactNode,
    ) => (
      <div className={`metric-group metric-group-${kind}`} key={kind}>
        <div className="metric-group-title">{title}</div>
        {children}
      </div>
    );

    const compactLine = (label: string, value: number | null | undefined) => (
      <div className="metric-compact-line" key={label}>
        <span className="metric-compact-label">{label}</span>
        <span className="metric-compact-value">{compactFmt(value)}</span>
      </div>
    );
    const compactValueLine = (value: number | null | undefined) => (
      <div className="metric-compact-line metric-compact-line-value-only" key="value">
        <span className="metric-compact-value">{compactFmt(value)}</span>
      </div>
    );
    const compactGroup = (
      label: string,
      kind: 'wall' | 'lumen' | 'length',
      children: React.ReactNode,
    ) => (
      <div className={`metric-compact-group metric-compact-group-${kind}`} key={kind}>
        <div className="metric-compact-title">{label}</div>
        {children}
      </div>
    );

    const expandedContent = isLesionStackViewLayout ? (
      <div className={`cross-section-metric-overlay metric-overlay-${analysisType} metric-overlay-structured`}>
        {metricGroup('Length', 'length', metricSingleValue(metricOverlay.length, 'mm'))}
      </div>
    ) : (
      <div className={`cross-section-metric-overlay metric-overlay-${analysisType} metric-overlay-structured`}>
        {analysisType === 'plaque' &&
          metricGroup(
            'Wall',
            'wall',
            <>
              {metricLine('Area', metricOverlay.wallArea, 'mm²')}
              {metricLine('Diameter', metricOverlay.wallDiameter, 'mm')}
            </>,
          )}
        {metricGroup(
          'Lumen',
          'lumen',
          <>
            {metricLine('Area', metricOverlay.lumenArea, 'mm²')}
            {metricLine('Diameter', metricOverlay.lumenDiameter, 'mm')}
          </>,
        )}
        {metricGroup('Length', 'length', metricSingleValue(metricOverlay.length, 'mm'))}
      </div>
    );

    const compactContent = isLesionStackViewLayout ? (
      <div className={`cross-section-metric-compact metric-compact-${analysisType}`}>
        {compactGroup('Length', 'length', compactValueLine(metricOverlay.length))}
      </div>
    ) : (
      <div className={`cross-section-metric-compact metric-compact-${analysisType}`}>
        {analysisType === 'plaque' &&
          compactGroup(
            'Wall',
            'wall',
            <>
              {compactLine('A', metricOverlay.wallArea)}
              {compactLine('D', metricOverlay.wallDiameter)}
            </>,
          )}
        {compactGroup(
          'Lumen',
          'lumen',
          <>
            {compactLine('A', metricOverlay.lumenArea)}
            {compactLine('D', metricOverlay.lumenDiameter)}
          </>,
        )}
        {compactGroup('Length', 'length', compactValueLine(metricOverlay.length))}
      </div>
    );

    return (
      <div className={`metric-overlay-hover-shell metric-overlay-${analysisType}`}>
        {compactContent}
        <div className="cross-section-metric-expanded">{expandedContent}</div>
      </div>
    );
  }, [analysisType, isLesionStackViewLayout, metricOverlay]);


  type CrossSectionOverlayKey =
    | 'lumen'
    | 'plaque'
    | 'fai'
    | 'lumenoutline'
    | 'plaqueoutline'
    | 'aorta'
    | 'avc';
  const shouldShowCrossSectionOverlayMenu = useMemo(
    () => ['plaque', 'stenosis', 'isr', 'fai', 'aorticValveCalcium'].includes(analysisType),
    [analysisType],
  );
  const [crossSectionOverlayMenuOpen, setCrossSectionOverlayMenuOpen] =
    useState(false);

  const crossSectionOverlayMenuItems: SimpleContextMenuItem[] = useMemo(() => {
    const overlayItems: Array<{
      key: CrossSectionOverlayKey | 'all';
      label: string;
      show: boolean;
    }> = analysisType === 'aorticValveCalcium'
      ? [
          { key: 'all', label: 'All', show: true },
          { key: 'aorta', label: 'Aorta', show: true },
          { key: 'avc', label: 'Aorta Calcium', show: true },
        ]
      : [
          { key: 'all', label: 'All', show: true },
          { key: 'lumen', label: 'Lumen', show: true },
          { key: 'plaque', label: 'Plaque', show: analysisType === 'plaque' },
          { key: 'fai', label: 'FAI', show: analysisType === 'fai' },
          { key: 'lumenoutline', label: 'LumenOutline', show: true },
          {
            key: 'plaqueoutline',
            label: 'PlaqueOutline',
            show: analysisType === 'plaque',
          },
        ];
    const visibleItems = overlayItems.filter((item) => item.show);
    const visibleKeys = visibleItems
      .map((item) => item.key)
      .filter((key): key is CrossSectionOverlayKey => key !== 'all');
    const allTrue =
      visibleKeys.length > 0 &&
      visibleKeys.every((key) => crossSectionOverlayLabel[key] !== false);

    const applyCrossSectionOverlayLabel = (
      nextLabel: Partial<Label>,
      changedKeys: CrossSectionOverlayKey[],
      forcedValue?: boolean,
    ) => {
      setData((prev: any) => ({
        viewOverlayLabels: {
          ...prev.viewOverlayLabels,
          crossSection: {
            ...prev.viewOverlayLabels?.crossSection,
            ...nextLabel,
          },
        },
      }));
      changedKeys.forEach((changedKey) => {
        labelorlineonoff(
          crossSectionAnalysisType,
          contextPool,
          changedKey,
          true,
          forcedValue ?? nextLabel[changedKey],
          'crossSection',
        );
      });
    };

    const toggle = (key: CrossSectionOverlayKey | 'all') => {
      if (key === 'all') {
        const nextValue = !allTrue;
        const nextLabel = { ...crossSectionOverlayLabel } as Partial<Label>;
        visibleKeys.forEach((visibleKey) => {
          nextLabel[visibleKey] = nextValue;
        });
        applyCrossSectionOverlayLabel(nextLabel, visibleKeys, nextValue);
        return;
      }

      const nextValue = !crossSectionOverlayLabel[key];
      const nextLabel = {
        ...crossSectionOverlayLabel,
        [key]: nextValue,
      } as Partial<Label>;
      applyCrossSectionOverlayLabel(nextLabel, [key], nextValue);
    };

    return visibleItems.map((item) => ({
      label: item.label,
      onClick: () => toggle(item.key),
      isChecked:
        item.key === 'all'
          ? allTrue
          : crossSectionOverlayLabel[item.key] !== false,
    }));
  }, [
    analysisType,
    crossSectionAnalysisType,
    crossSectionOverlayLabel,
    contextPool,
    setData,
  ]);

  const crossSectionOverlayButtonContent = useMemo(() => {
    if (!shouldShowCrossSectionOverlayMenu) return null;
    return (
      <div
        className={`projection-text ${panelConfig.componentKey}`}
        onClick={(event) => {
          event.stopPropagation();
          setCrossSectionOverlayMenuOpen(true);
        }}
      >
        Overlays
      </div>
    );
  }, [panelConfig.componentKey, shouldShowCrossSectionOverlayMenu]);

  const renderTopLeftContent = useMemo(() => {
    if (!metricOverlay?.plaquePercent) return;

    // const match = className.match(/^CS(\d+)-\d$/);
    // if (!match) return undefined;
    // const myIndex = CPRType[cprMode.name];
    // const id = paramData?.SavedInfo.Coronary.main_branch_id?.[Number(match[1]) - 1];

    const present = metricOverlay.plaquePercent;

    const density = typeof document !== 'undefined'
      ? document.documentElement.dataset.displayDensity
      : undefined;
    const isCompactViewport =
      density !== '4k' &&
      typeof window !== 'undefined' &&
      (window.innerWidth <= 1600 || window.innerHeight <= 1000);
    const isTinyCrossSection =
      isCompactViewport || dimensions.width < 180 || dimensions.height < 155;
    const chartWidth = density === '4k'
      ? Math.min(300, Math.max(240, dimensions.width - 10 || 270))
      : isTinyCrossSection
        ? Math.min(108, Math.max(94, dimensions.width - 16 || 104))
        : Math.min(220, Math.max(180, dimensions.width - 10 || 210));
    const chartHeight = density === '4k' ? 102 : isTinyCrossSection ? 46 : 78;

    return (
      <div className={`projection-text cross-section-plaque-bar ${isTinyCrossSection ? 'is-compact' : ''}`}>
        <MultiBarChart
          width={chartWidth}
          height={chartHeight}
          data={[
            {
              label: 'Low attenuation Plaque',
              value: present?.lowattenuationPersent! * 100,
            },
            {
              label: 'Noncalcified Plaque',
              value: present?.noncalcifiedPersent! * 100,
            },
            {
              label: 'Calcified Plaque',
              value: present?.calcifiedPersent! * 100,
            },
            { label: 'Lumen', value: present?.lumenPersent! * 100 },
          ]}
        />
      </div>
    );
  }, [dimensions.height, dimensions.width, metricOverlay]);

  const isSelectedPanel = selectedPanelClassName === className;

  const bottomLeftContent = useMemo(() => {
    const huText = (shouldUseServerCrossSectionCanvas || isSelectedPanel) && hoverHU !== null ? hoverHU.toFixed(1) : '-';
    const modeLabel = taviPanelTitle ?? cprMode.name;
    const hideHuTextForTaviFixed =
      shouldUseCrossSectionImageShell ||
      (analysisType === 'tavi' &&
        ((mainIndex === 1 && subIndex >= 0 && subIndex <= 2) ||
          (mainIndex === 2 && subIndex >= 0 && subIndex <= 2)));


    return (
      <>
        <div className={`projection-text ${modeLabel}` }>
          {!hideHuTextForTaviFixed && (
            <div className="plain-text" style={{ fontSize: '0.75rem' }}>
              HU: {huText}
            </div>
          )}
          {modeLabel}
        </div>
        {crossSectionOverlayButtonContent}
      </>
    );
  }, [
    cprMode,
    hoverHU,
    isSelectedPanel,
    taviPanelTitle,
    analysisType,
    mainIndex,
    subIndex,
    shouldUseCrossSectionImageShell,
    shouldUseServerCrossSectionCanvas,
    crossSectionOverlayButtonContent,
  ]);

  const classNameWithBranchName = useMemo(
    () =>
      `renderer CrossSection ${branchIds
        .filter((val) => !!val[branchId])
        .flatMap((v) => Object.values(v)[0])
        .join('-')}-${mainIndex}-${subIndex}`,
    [branchIds, branchId, mainIndex, subIndex],
  );

  const getIndexOnCrossSection = useCallback((point: {
    clientX: number;
    clientY: number;
  }) => {
      if (!context.current) return null;
    const { renderer, renderWindow, canvas, imageData, CSReslice, actorIds } = context.current;
      if (!renderer || !renderWindow || !canvas || !imageData || !CSReslice) {
        return null;
      }

      const bounds = canvas.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return null;

      const scaleX = canvas.width / bounds.width;
      const scaleY = canvas.height / bounds.height;
      const x = Math.floor(scaleX * (point.clientX - bounds.left));
    const y = Math.floor(scaleY * (bounds.height - point.clientY + bounds.top));

      const csActor = actorIds['CSActor'];
      if (!csActor) return null;
      const actorBounds = csActor.getBounds();
      if (!actorBounds) return null;
    const openGLRenderWindow = renderWindow.getViews()[0] as vtkOpenGLRenderWindow;

      const worldCorners: [number, number, number][] = [
        [actorBounds[0], actorBounds[2], actorBounds[4]],
        [actorBounds[1], actorBounds[2], actorBounds[4]],
        [actorBounds[1], actorBounds[3], actorBounds[4]],
        [actorBounds[0], actorBounds[3], actorBounds[4]],
      ];
      const displayCorners = worldCorners.map((corner) => {
        const disp = openGLRenderWindow.worldToDisplay(
          corner[0],
          corner[1],
          corner[2],
          renderer,
        ) as [number, number, number];
        return [disp[0], disp[1]] as [number, number];
      });

      const pt: [number, number] = [x, y];
      const cross = (
        a: [number, number],
        b: [number, number],
        p: [number, number],
      ) => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
      const signs = displayCorners.map((corner, idx) =>
        cross(corner, displayCorners[(idx + 1) % displayCorners.length], pt),
      );
      const allNonNeg = signs.every((v) => v >= -0.5);
      const allNonPos = signs.every((v) => v <= 0.5);
      if (!allNonNeg && !allNonPos) return null;

      const selector = vtkOpenGLHardwareSelector.newInstance({
        captureZValues: true,
        fieldAssociation: FieldAssociations.FIELD_ASSOCIATION_POINTS_THEN_CELLS,
      });
      let info: any = null;
      try {
        selector.attach(openGLRenderWindow, renderer);
        selector.setArea(x - 0.5, y - 0.5, x + 0.5, y + 0.5);
        if (!selector.captureBuffers()) return null;
        info = selector.getPixelInformation([x, y], 1, [0, 0]);
      } finally {
        selector.delete?.();
      }
      let displayX = x;
      let displayY = y;
      let displayZ: number | null = null;

      if (info && Number.isFinite(info.zValue)) {
        displayX = info.displayPosition[0];
        displayY = info.displayPosition[1];
        displayZ = info.zValue;
      } else {
        const gl = canvas.getContext('webgl2');
        if (!gl) return null;
        const size = openGLRenderWindow.getSize();
        const height = size[1];
        const flipY = Math.max(0, Math.min(height - 1, height - y));
        const depth = new Float32Array(1);
        gl.readPixels(
          Math.max(0, x),
          flipY,
          1,
          1,
          gl.DEPTH_COMPONENT,
          gl.FLOAT,
          depth,
        );
        if (!Number.isFinite(depth[0]) || depth[0] >= 0.999999) return null;
        displayZ = depth[0];
      }
      if (displayZ === null) return null;

      // Picker result for upscaled cross-section is in displayed 2D image space.
      const displayWorld = openGLRenderWindow.displayToWorld(
        displayX,
        displayY,
        displayZ,
        renderer,
      ) as [number, number, number];

      // Undo local pixel upscale (2^upscale) to get original reslice ij.
      const upscaleStep = isAorticMode ? 0 : config.upscale;
      const scale = Math.pow(2, Math.max(0, upscaleStep));
      // CSActor local origin is not guaranteed to be (0,0). Offset by actor bounds.
      const i = (displayWorld[0] - actorBounds[0]) / scale;
      const j = (displayWorld[1] - actorBounds[2]) / scale;

    const outputOrigin = CSReslice.getOutputOrigin() as [number, number, number];
    const outputSpacing = CSReslice.getOutputSpacing() as [number, number, number];
      const resliceAxes = CSReslice.getResliceAxes() as number[];
      const outputExtent = CSReslice.getOutputExtent() as [
        number,
        number,
        number,
        number,
        number,
        number,
      ];
      if (
        i < outputExtent[0] - 0.5 ||
        i > outputExtent[1] + 0.5 ||
        j < outputExtent[2] - 0.5 ||
        j > outputExtent[3] + 0.5
      ) {
        return null;
      }

      const outX = outputOrigin[0] + i * outputSpacing[0];
      const outY = outputOrigin[1] + j * outputSpacing[1];
      const outZ = 0;

      // mat4 (column-major): world = Axes * [outX, outY, outZ, 1]
      const worldX =
        resliceAxes[0] * outX +
        resliceAxes[4] * outY +
        resliceAxes[8] * outZ +
        resliceAxes[12];
      const worldY =
        resliceAxes[1] * outX +
        resliceAxes[5] * outY +
        resliceAxes[9] * outZ +
        resliceAxes[13];
      const worldZ =
        resliceAxes[2] * outX +
        resliceAxes[6] * outY +
        resliceAxes[10] * outZ +
        resliceAxes[14];

      const imageDataAny = imageData as any;
      const outIdx: [number, number, number] = [0, 0, 0];
    const converted = imageDataAny.worldToIndex?.([worldX, worldY, worldZ], outIdx);
      const idx = (Array.isArray(converted) ? converted : outIdx) as [
        number,
        number,
        number,
      ];
      const dims = imageData.getDimensions() as [number, number, number];

      // Outside actual dicom volume => treat as invalid (black area or non-image hit).
      if (
        !Number.isFinite(idx[0]) ||
        !Number.isFinite(idx[1]) ||
        !Number.isFinite(idx[2]) ||
        idx[0] < 0 ||
        idx[1] < 0 ||
        idx[2] < 0 ||
        idx[0] >= dims[0] ||
        idx[1] >= dims[1] ||
        idx[2] >= dims[2]
      ) {
        return null;
      }

      return idx;
  }, [config.upscale, isAorticMode]);

  const sampleHUAtIndex = useCallback((idx: [number, number, number] | null) => {
      if (!idx || !context.current?.imageData) return null;
      const imageData = context.current.imageData;
      const dims = imageData.getDimensions() as [number, number, number];
      const scalars = imageData.getPointData().getScalars();
      if (!scalars) return null;
      const data = scalars.getData() as ArrayLike<number>;
      if (!data || !data.length) return null;

      const x0 = Math.floor(idx[0]);
      const y0 = Math.floor(idx[1]);
      const z0 = Math.floor(idx[2]);
      const x1 = Math.min(x0 + 1, dims[0] - 1);
      const y1 = Math.min(y0 + 1, dims[1] - 1);
      const z1 = Math.min(z0 + 1, dims[2] - 1);

      if (
        x0 < 0 ||
        y0 < 0 ||
        z0 < 0 ||
        x0 >= dims[0] ||
        y0 >= dims[1] ||
        z0 >= dims[2]
      ) {
        return null;
      }

      const fx = idx[0] - x0;
      const fy = idx[1] - y0;
      const fz = idx[2] - z0;

      const at = (x: number, y: number, z: number) =>
        data[z * dims[0] * dims[1] + y * dims[0] + x];

      const c000 = at(x0, y0, z0);
      const c100 = at(x1, y0, z0);
      const c010 = at(x0, y1, z0);
      const c110 = at(x1, y1, z0);
      const c001 = at(x0, y0, z1);
      const c101 = at(x1, y0, z1);
      const c011 = at(x0, y1, z1);
      const c111 = at(x1, y1, z1);

      const c00 = c000 * (1 - fx) + c100 * fx;
      const c10 = c010 * (1 - fx) + c110 * fx;
      const c01 = c001 * (1 - fx) + c101 * fx;
      const c11 = c011 * (1 - fx) + c111 * fx;
      const c0 = c00 * (1 - fy) + c10 * fy;
      const c1 = c01 * (1 - fy) + c11 * fy;
      const hu = c0 * (1 - fz) + c1 * fz;

      return Number.isFinite(hu) ? hu : null;
  }, []);

  const getOutlineHitInfo = useCallback((evt: MouseEvent) => {
      if (!shouldShowOutlineFeatures) return null;
      if (!context.current) return null;
      const { actorIds, renderer, renderWindow, canvas } = context.current;
      const outlineActor = actorIds['allLabelOutlineTest'] as vtkActor;
      const handleActor = actorIds['allLabelOutlineTestHandles'] as vtkActor;
      if (!outlineActor) return null;
      const mapper = outlineActor.getMapper() as vtkMapper;
      const input = mapper?.getInputData() as vtkPolyData;
    const points = input?.getPoints()?.getData?.() as Float32Array | undefined;
      if (!points || points.length < 3) return null;
      const handleMapper = handleActor?.getMapper() as vtkMapper | undefined;
    const handleInput = handleMapper?.getInputData() as vtkPolyData | undefined;
    const handlePoints = handleInput
      ?.getPoints()
      ?.getData?.() as Float32Array | undefined;

      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const displayX = Math.floor(scaleX * (evt.clientX - rect.left));
    const displayY = Math.floor(scaleY * (rect.height - evt.clientY + rect.top));

    const openGLRenderWindow = renderWindow.getViews()[0] as vtkOpenGLRenderWindow;
      const actorBounds = outlineActor.getBounds();
      if (!actorBounds) return null;
      const zDisplay = openGLRenderWindow.worldToDisplay(
        0,
        0,
        actorBounds[4],
        renderer,
      )[2];
      const world = openGLRenderWindow.displayToWorld(
        displayX,
        displayY,
        zDisplay,
        renderer,
      ) as [number, number, number];

      const pos = outlineActor.getPosition() as [number, number, number];
      const camera = renderer.getActiveCamera();
      const worldPerPixel =
        (2 * camera.getParallelScale()) / Math.max(1, canvas.height);
    const lineThresholdSq = (worldPerPixel * 12) * (worldPerPixel * 12);
    const vertexThresholdSq = (worldPerPixel * 10) * (worldPerPixel * 10);

      let minDistSq = Number.POSITIVE_INFINITY;
      let minVertexIdx = -1;
    const vertexSource = handlePoints && handlePoints.length >= 3 ? handlePoints : points;
      for (let i = 0; i < vertexSource.length; i += 3) {
        const dx = vertexSource[i] + pos[0] - world[0];
        const dy = vertexSource[i + 1] + pos[1] - world[1];
        const d2 = dx * dx + dy * dy;
        if (d2 < minDistSq) {
          minDistSq = d2;
          minVertexIdx = i / 3;
        }
      }

      if (minDistSq <= vertexThresholdSq && minVertexIdx >= 0) {
        return {
          kind: 'vertex' as const,
          actor: outlineActor,
          input,
          pointData: vertexSource,
          vertexIndex: minVertexIdx,
        };
      }
      if (minDistSq <= lineThresholdSq) {
        return {
          kind: 'line' as const,
          actor: outlineActor,
          input,
          pointData: points,
          vertexIndex: -1,
        };
      }
      return null;
  }, [shouldShowOutlineFeatures]);

  useEffect(() => {
    if (shouldUseServerCrossSectionCanvas) return;
    if (!isSelectedPanel) {
      setHoverHU(null);
      return;
    }
    setHoverHU(sampleHUAtIndex(hoverIndexCoord));
  }, [hoverIndexCoord, isSelectedPanel, sampleHUAtIndex, shouldUseServerCrossSectionCanvas]);

  useEffect(() => {
    scheduleTaviRelativeOutlineOverlayUpdate();
  }, [scheduleTaviRelativeOutlineOverlayUpdate, dimensions.width, dimensions.height]);

  useEffect(() => {
    if (!shouldShowAnnulusPointEdit) {
      annulusDraggingHandleIndexRef.current = -1;
      return;
    }
    if (
      !annulusEditablePoints &&
      taviRelativeOutlineOverlay &&
      taviRelativeOutlineOverlay.length >= 2
    ) {
      const src = [...taviRelativeOutlineOverlay];
      const first = src[0];
      const last = src[src.length - 1];
      const isClosed =
        Math.abs(first[0] - last[0]) < 0.001 && Math.abs(first[1] - last[1]) < 0.001;
      setAnnulusEditablePoints(isClosed ? src.slice(0, -1) : src);
    }
  }, [
    shouldShowAnnulusPointEdit,
    annulusEditablePoints,
    taviRelativeOutlineOverlay,
  ]);

  useEffect(() => {
    if (!shouldShowAnnulusPointEdit) return;
    const onMouseMove = (evt: MouseEvent) => {
      const idx = annulusDraggingHandleIndexRef.current;
      if (idx < 0) return;
      const bounds = ref.current?.getBoundingClientRect();
      if (!bounds || !bounds.width || !bounds.height) return;
      const x = Math.min(Math.max(evt.clientX - bounds.left, 0), bounds.width);
      const y = Math.min(Math.max(evt.clientY - bounds.top, 0), bounds.height);
      setAnnulusEditablePoints((prev) => {
        if (!prev || idx >= prev.length) return prev;
        const next = [...prev];
        next[idx] = [x, y];
        return next;
      });
      evt.preventDefault();
      evt.stopPropagation();
    };
    const onMouseUp = () => {
      annulusDraggingHandleIndexRef.current = -1;
    };
    window.addEventListener('mousemove', onMouseMove, { passive: false });
    window.addEventListener('mouseup', onMouseUp, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [shouldShowAnnulusPointEdit]);

  useEffect(() => {
    if (analysisType !== 'tavi' || mainIndex !== 2) {
      setTaviHeightLineOverlay(null);
      return;
    }
    requestAnimationFrame(() => {
      updateTaviHeightLineOverlay();
      requestAnimationFrame(() => {
        updateTaviHeightLineOverlay();
      });
    });
  }, [
    analysisType,
    mainIndex,
    subIndex,
    updateTaviHeightLineOverlay,
    dimensions.width,
    dimensions.height,
    (pool as Record<string, any> | undefined)?.['tavi.parsed']?.loaded,
  ]);

  useEffect(() => {
    if (shouldUseCrossSectionImageShell || shouldUseServerCrossSectionCanvas) return;
    if (!context.current || !ref.current) return;
    if (isTaviFixedPanel && !shouldShowOutlineFeatures) return;
    const container = ref.current;
    const wheelHandler = (evt: WheelEvent) => {
      if (isLesionStackViewLayout) {
        if (evt.cancelable) evt.preventDefault();
        const delta = evt.deltaY > 0 ? 1 : -1;
        if (isLesionStackMiddlePanel) {
          dispatchLesionStackMiddleAnchorDelta(delta);
        } else {
          dispatchCrossSectionImageIndex(
            Number(info?.index ?? fallbackIndex) + delta,
            effectiveSourceSubIndex,
          );
        }
        return;
      }

      handleMouseWheel(
        context,
        evt,
        crossSectionAnalysisType,
        mainIndex,
        effectiveSourceSubIndex,
        branchIndices as [number, number, number],
      );
    };
    // const mouseMoveHandler = (evt: MouseEvent) => handleMouseMove(context,evt);
    const mouseMoveHandler = (evt: MouseEvent) => {
      if (shouldShowOutlineFeatures && outlineDragRef.current.isDragging) {
        if (!context.current) return;
        const { actorIds, renderer } = context.current;
        const outlineActor = actorIds['allLabelOutlineTest'] as vtkActor;
        if (!outlineActor) {
          outlineDragRef.current = {
            isDragging: false,
            lastClientX: 0,
            lastClientY: 0,
            mode: 'none',
            vertexIndex: -1,
          };
          return;
        }
        const deltaX = evt.clientX - outlineDragRef.current.lastClientX;
        const deltaY = evt.clientY - outlineDragRef.current.lastClientY;
        outlineDragRef.current.lastClientX = evt.clientX;
        outlineDragRef.current.lastClientY = evt.clientY;

        const worldPerPixel =
          (2 * renderer.getActiveCamera().getParallelScale()) /
          Math.max(1, context.current.canvas.height);
        const moveX = deltaX * worldPerPixel;
        const moveY = -deltaY * worldPerPixel;
        if (outlineDragRef.current.mode === 'vertex') {
          const mapper = outlineActor.getMapper() as vtkMapper;
          const input = mapper?.getInputData() as vtkPolyData;
          const pointData = input
            ?.getPoints()
            ?.getData?.() as Float32Array | undefined;
          const pointIdx = outlineDragRef.current.vertexIndex * 3;
          if (
            pointData &&
            outlineDragRef.current.vertexIndex >= 0 &&
            pointIdx + 1 < pointData.length
          ) {
            pointData[pointIdx] += moveX;
            pointData[pointIdx + 1] += moveY;
            input.getPoints().modified();
            input.modified();

            const handleActor = actorIds[
              'allLabelOutlineTestHandles'
            ] as vtkActor;
            const handleMapper = handleActor?.getMapper() as vtkMapper;
            const handleInput = handleMapper?.getInputData() as vtkPolyData;
            const handlePointData = handleInput
              ?.getPoints()
              ?.getData?.() as Float32Array | undefined;
            if (handlePointData && pointIdx + 1 < handlePointData.length) {
              handlePointData[pointIdx] += moveX;
              handlePointData[pointIdx + 1] += moveY;
              handleInput.getPoints().modified();
              handleInput.modified();
            }
          }
        } else {
          const pos = outlineActor.getPosition();
          outlineActor.setPosition(pos[0] + moveX, pos[1] + moveY, pos[2]);
          const handleActor = actorIds['allLabelOutlineTestHandles'] as vtkActor;
          if (handleActor) {
            const hpos = handleActor.getPosition();
            handleActor.setPosition(hpos[0] + moveX, hpos[1] + moveY, hpos[2]);
          }
        }
        scheduleRender(rendererId, { type: 'updateRenderWindow' });
        refreshOutlineMeasurements();
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }

      handleMouseMove({
        context,
        evt,
        toolName:panelToolState?.activeToolId,
        analysisType: crossSectionAnalysisType,
        mainIndex,
        subIndex: effectiveSourceSubIndex,
        indices:branchIndices as [number, number, number],
        voi:currentVOI,
        updateWindowLevelWidth
      });
      if (!isSelectedPanel) {
        if (hoverPickRafRef.current !== null) {
          window.cancelAnimationFrame(hoverPickRafRef.current);
          hoverPickRafRef.current = null;
        }
        hoverPickPointRef.current = null;
        setHoverIndexCoord(null);
        setHoverHU(null);
        return;
      }
      hoverPickPointRef.current = {
        clientX: evt.clientX,
        clientY: evt.clientY,
      };
      if (hoverPickRafRef.current === null) {
        hoverPickRafRef.current = window.requestAnimationFrame(() => {
          hoverPickRafRef.current = null;
          const point = hoverPickPointRef.current;
          setHoverIndexCoord(point ? getIndexOnCrossSection(point) : null);
        });
      }
    };
    const mouseDownHandler = (evt: MouseEvent) => {
      const hitInfo =
        shouldShowOutlineFeatures && evt.button === 0
          ? getOutlineHitInfo(evt)
          : null;
      if (hitInfo) {
        outlineDragRef.current = {
          isDragging: true,
          lastClientX: evt.clientX,
          lastClientY: evt.clientY,
          mode: hitInfo.kind === 'vertex' ? 'vertex' : 'translate',
          vertexIndex: hitInfo.vertexIndex,
        };
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      handleMouseDown(context, evt);
    };
    const mouseUpHandler = (evt: MouseEvent) => {
      if (outlineDragRef.current.isDragging) {
        outlineDragRef.current = {
          isDragging: false,
          lastClientX: 0,
          lastClientY: 0,
          mode: 'none',
          vertexIndex: -1,
        };
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
      handleMouseUp(context, evt);
    };
    const mouseLeaveHandler = () => {
      outlineDragRef.current = {
        isDragging: false,
        lastClientX: 0,
        lastClientY: 0,
        mode: 'none',
        vertexIndex: -1,
      };
      if (hoverPickRafRef.current !== null) {
        window.cancelAnimationFrame(hoverPickRafRef.current);
        hoverPickRafRef.current = null;
      }
      hoverPickPointRef.current = null;
      setHoverIndexCoord(null);
      setHoverHU(null);
    };
    container.addEventListener('wheel', wheelHandler, { passive: false });
    container.addEventListener('mousedown', mouseDownHandler, {
      passive: false,
    });
    container.addEventListener('mouseup', mouseUpHandler, { passive: false });
    container.addEventListener('mousemove', mouseMoveHandler, {
      passive: false,
    });
    container.addEventListener('mouseleave', mouseLeaveHandler, {
      passive: true,
    });

    return () => {
      container.removeEventListener('wheel', wheelHandler);
      container.removeEventListener('mousemove', mouseMoveHandler);
      container.removeEventListener('mousedown', mouseDownHandler);
      container.removeEventListener('mouseup', mouseUpHandler);
      container.removeEventListener('mouseleave', mouseLeaveHandler);
    };
  }, [
    context,
    isDataReady,
    analysisType,
    branchIndices,
    panelToolState,
    updateWindowLevelWidth,
    currentVOI,
    isSelectedPanel,
    getIndexOnCrossSection,
    getOutlineHitInfo,
    rendererId,
    refreshOutlineMeasurements,
    isTaviFixedPanel,
    shouldShowOutlineFeatures,
    shouldUseCrossSectionImageShell,
    shouldUseServerCrossSectionCanvas,
    dispatchCrossSectionImageIndex,
    dispatchLesionStackMiddleAnchorDelta,
    effectiveSourceSubIndex,
    fallbackIndex,
    info?.index,
    isLesionStackViewLayout,
    isLesionStackMiddlePanel,
  ]);

  useEffect(() => {
    if (!isTaviFixedPanel) {
      fixedPanelZoomAppliedKeyRef.current = null;
    }
  }, [isTaviFixedPanel]);

  useEffect(() => {
    if (!isTaviAnnulusPanel) {
      setAnnulusEditablePoints(null);
      annulusDraggingHandleIndexRef.current = -1;
    }
  }, [isTaviAnnulusPanel, selectedSeriesId, branchId]);

  useEffect(() => {
    if (!context.current) return;
    if (shouldShowOutlineFeatures) return;
    const { actorIds, renderer, renderWindow } = context.current;
    const removableKeys = [
      'allLabelOutlineTest',
      'allLabelOutlineTestHandles',
      'allLabelLongAxis',
      'allLabelShortAxis',
    ] as const;
    let removed = false;
    removableKeys.forEach((key) => {
      const actor = actorIds[key] as vtkActor | undefined;
      if (actor) {
        renderer.removeActor(actor);
        delete actorIds[key];
        removed = true;
      }
    });
    if (removed) {
      renderWindow?.render?.();
    }
  }, [shouldShowOutlineFeatures, context]);

  const renderedAnnulusOutlinePoints = useMemo(() => {
    if (!shouldShowAnnulusPointEdit || !annulusEditablePoints || annulusEditablePoints.length < 2) {
      return taviRelativeOutlineOverlay;
    }
    const first = annulusEditablePoints[0];
    return [...annulusEditablePoints, first];
  }, [shouldShowAnnulusPointEdit, annulusEditablePoints, taviRelativeOutlineOverlay]);

  return (
    <div
      className={`panel ${className}`}
      onMouseDown={() => {
        updateSelectedPanelConfig({
          ...panelConfig,
          componentKey: panelConfig.componentKey,
          className: className,
        });
      }}
    >
      <div className={classNameWithBranchName} ref={ref}>
        {shouldUseServerCrossSectionCanvas && !isLesionStackHiddenMiddlePanel && (
          <ServerCrossSectionCanvas
            className={className}
            imageData={serverCanvasResources?.imageData}
            labelData={serverCanvasResources?.labelData}
            axes={serverCanvasResources?.axes}
            index={imageIndex}
            analysisType={crossSectionAnalysisType}
            voi={currentVOI}
            config={config}
            baseWidth={serverCanvasResources?.baseWidth ?? Math.max(8, config.basewidth)}
            upscale={serverCanvasResources?.upscale ?? 0}
            activeToolId={panelToolState?.activeToolId}
            visibility={crossSectionImageLayerVisibility}
            windowingPolicy={crossSectionImageWindowingPolicy}
            onWheelIndex={handleServerCrossSectionCanvasWheelIndex}
            onScrollDragIndex={(delta) =>
              handleCrossSectionImageScrollDrag(0, delta)
            }
            onWindowLevelChange={handleCrossSectionImageWindowLevelChange}
            rendererId={rendererId}
            initialZoomScale={serverCrossSectionInitialZoomScale}
            slabThicknessMm={serverCrossSectionSlabThicknessMm}
            onHoverHU={setHoverHU}
          />
        )}
        {shouldUseCrossSectionImageShell && !isLesionStackHiddenMiddlePanel && (
          <ImageLayerStack
            layers={crossSectionImageLayers}
            revision={imageManifest?.revision}
            width={
              crossSectionImageStack?.width ?? Math.max(1, dimensions.width)
            }
            height={
              crossSectionImageStack?.height ?? Math.max(1, dimensions.height)
            }
            seriesId={selectedSeriesId}
            accessToken={accessToken}
            prefetchLayers={crossSectionImagePrefetchLayers}
            prefetchConcurrency={isCprMarkerPreviewActive ? 0 : 4}
            priority={isCprMarkerPreviewActive ? 20 : 50}
            loading={!crossSectionImageLayers?.base}
            disableStaleFallback
            enableViewportTransform
            activeToolId={panelToolState?.activeToolId}
            visibility={crossSectionImageLayerVisibility}
            overlayPolicy={crossSectionImageOverlayPolicy}
            windowingPolicy={crossSectionImageWindowingPolicy}
            cacheScope={crossSectionImageCacheScope}
            diagnosticLabel={crossSectionImageDiagnostic}
            onWheel={handleCrossSectionImageWheel}
            onScrollDrag={handleCrossSectionImageScrollDrag}
            onWindowLevelChange={handleCrossSectionImageWindowLevelChange}
          />
        )}
      </div>
      {shouldShowOutlineFeatures && axisOverlay && (
        <svg
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 7,
          }}
        >
          <line
            x1={axisOverlay.long[0][0]}
            y1={axisOverlay.long[0][1]}
            x2={axisOverlay.long[1][0]}
            y2={axisOverlay.long[1][1]}
            stroke="#ff5a5a"
            strokeWidth={1.5}
          />
          <line
            x1={axisOverlay.short[0][0]}
            y1={axisOverlay.short[0][1]}
            x2={axisOverlay.short[1][0]}
            y2={axisOverlay.short[1][1]}
            stroke="#ff5a5a"
            strokeWidth={1.5}
          />
        </svg>
      )}
      {!isAnyFullscreenActive &&
        analysisType === 'tavi' &&
        mainIndex === 1 &&
        renderedAnnulusOutlinePoints &&
        renderedAnnulusOutlinePoints.length >= 2 &&
        renderedAnnulusOutlinePoints.every((p) => isFinite2DPoint(p)) && (
          <svg
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 6,
            }}
          >
            <polyline
              points={renderedAnnulusOutlinePoints
                .map(([x, y]) => `${x},${y}`)
                .join(' ')}
              fill="none"
              stroke="#3dff4f"
              strokeWidth={1.2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {shouldShowAnnulusPointEdit &&
              annulusEditablePoints &&
              annulusEditablePoints.map((pt, idx) => (
                <circle
                  key={`annulus-edit-point-${idx}`}
                  cx={pt[0]}
                  cy={pt[1]}
                  r={2.6}
                  fill="#3dff4f"
                  stroke="#0a0a0a"
                  strokeWidth={0.8}
                  style={{ cursor: 'pointer', pointerEvents: 'all' }}
                  onMouseDown={(evt) => {
                    annulusDraggingHandleIndexRef.current = idx;
                    evt.preventDefault();
                    evt.stopPropagation();
                  }}
                />
              ))}
          </svg>
        )}
      {!isAnyFullscreenActive &&
        analysisType === 'tavi' &&
        mainIndex === 1 &&
        taviRelativeExtraLinesOverlay &&
        taviRelativeExtraLinesOverlay.length > 0 &&
        taviRelativeExtraLinesOverlay.every(
          (line) => isFinite2DPoint(line[0]) && isFinite2DPoint(line[1]),
        ) && (
          <svg
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 7,
            }}
          >
            {taviRelativeExtraLinesOverlay.map((line, idx) => (
              <line
                key={`tavi-extra-line-${idx}`}
                x1={line[0][0]}
                y1={line[0][1]}
                x2={line[1][0]}
                y2={line[1][1]}
                stroke={
                  subIndex === 1
                    ? ['#57ff57', '#4fc3ff', '#ffd84d'][idx % 3] // sinus: 3 distinct colors
                    : ['#ff6b6b', '#4fb7ff'][idx % 2] // annulus/junction: long vs short
                }
                strokeWidth={1.2}
                strokeDasharray="4 3"
              />
            ))}
          </svg>
        )}
      {!isAnyFullscreenActive &&
        analysisType === 'tavi' &&
        mainIndex === 2 &&
        (subIndex === 0 || subIndex === 1 || subIndex === 2) &&
        taviHeightLineOverlay &&
        isFinite2DPoint(taviHeightLineOverlay.annulusLine[0]) &&
        isFinite2DPoint(taviHeightLineOverlay.annulusLine[1]) &&
        isFinite2DPoint(taviHeightLineOverlay.connector[0]) &&
        isFinite2DPoint(taviHeightLineOverlay.connector[1]) &&
        isFinite2DPoint(taviHeightLineOverlay.ostium) &&
        isFinite2DPoint(taviHeightLineOverlay.projected) &&
        isFinite2DPoint(taviHeightLineOverlay.cusp) && (
          <svg
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 7,
            }}
          >
            <line
              x1={taviHeightLineOverlay.annulusLine[0][0]}
              y1={taviHeightLineOverlay.annulusLine[0][1]}
              x2={taviHeightLineOverlay.annulusLine[1][0]}
              y2={taviHeightLineOverlay.annulusLine[1][1]}
              stroke="#37e9ff"
              strokeWidth={1.8}
              strokeDasharray="8 6"
            />
            <line
              x1={taviHeightLineOverlay.connector[0][0]}
              y1={taviHeightLineOverlay.connector[0][1]}
              x2={taviHeightLineOverlay.connector[1][0]}
              y2={taviHeightLineOverlay.connector[1][1]}
              stroke="#ffb347"
              strokeWidth={2}
            />
            {/* <circle
              cx={taviHeightLineOverlay.ostium[0]}
              cy={taviHeightLineOverlay.ostium[1]}
              r={2}
              fill="#ff4d4f"
              stroke="#ff4d4f"
            />
            <circle
              cx={taviHeightLineOverlay.projected[0]}
              cy={taviHeightLineOverlay.projected[1]}
              r={2}
              fill="#ff4d4f"
              stroke="#ff4d4f"
            />
            <circle
              cx={taviHeightLineOverlay.cusp[0]}
              cy={taviHeightLineOverlay.cusp[1]}
              r={2}
              fill="#ff4d4f"
              stroke="#ff4d4f"
            /> */}
          </svg>
        )}
      {showOutlineMetricsBox && shouldShowOutlineFeatures && outlineMetrics && (
        <div
          style={{
            position: 'absolute',
            right: '4%',
            top: '22%',
            minWidth: '150px',
            background: 'rgba(20,20,20,0.82)',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: '6px',
            padding: '8px 10px',
            color: '#fff',
            fontSize: '12px',
            lineHeight: 1.35,
            pointerEvents: 'none',
            zIndex: 8,
          }}
        >
          <div>Long axis: {outlineMetrics.longAxis.toFixed(2)} mm</div>
          <div>Short axis: {outlineMetrics.shortAxis.toFixed(2)} mm</div>
          <div>Area: {outlineMetrics.area.toFixed(2)} mm²</div>
          <div>Diameter: {outlineMetrics.diameter.toFixed(2)} mm</div>
        </div>
      )}
      {globalToolState?.toggles.ToggleAnnotations && !isLesionStackHiddenMiddlePanel && (
        <>
          <PanelOverlay
            config={{
              'top-left': {
                content:
                  analysisType === 'plaque' && !isLesionStackViewLayout
                    ? renderTopLeftContent
                    : null,
              },
              'bottom-left': {
                content: isLesionStackViewLayout ? null : bottomLeftContent,
              },
              'bottom-right': {
                content: renderBottomRightContent,
              },
            }}
            originComponentKey={panelConfig.componentKey}
            className={className}
          />
        </>
      )}
      {crossSectionOverlayMenuOpen && (
        <SimpleContextMenu
          items={crossSectionOverlayMenuItems}
          onClose={() => setCrossSectionOverlayMenuOpen(false)}
          checkbox={true}
        />
      )}
    </div>
  );
};

export default CrossSectionPanel;
